import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Zap, Key, GitBranch, FileText, Brain, Plus, RefreshCw, Copy, CheckCircle2,
  Trash2, ToggleLeft, ToggleRight, TestTube2, AlertCircle, LayoutDashboard,
  Send, BarChart3, MessageSquare, Upload, Users, Download, Eye, EyeOff,
  Gauge, Wifi, KeyRound, Loader2, Pencil, Save, X,
} from 'lucide-react';

import { WhatsAppAutomationService, EMPTY_STATS } from '../../services/whatsappAutomationService';
import { ENV } from '../../config/env';
import WhatsApp from './WhatsApp';
import MetaTemplateCard, { TemplateListItem } from '../../components/admin/MetaTemplateCard';
import WhatsAppTemplatePreview from '../../components/admin/WhatsAppTemplatePreview';
import {
  BULK_VARIABLES, parseBulkMessageCsv, downloadBulkCsvSample, recipientToVariables,
  suggestTemplateParams,
} from '../../utils/bulkMessageCsv';
import {
  ROADSERVE_WHATSAPP_AI_PROMPT,
  isLegacyAiPrompt,
} from '../../constants/whatsappAiKnowledge';

const SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', short: 'Home', icon: LayoutDashboard },
  { id: 'chats', label: 'Chats', short: 'Chats', icon: MessageSquare },
  { id: 'accounts', label: 'Automation Keys', short: 'Keys', icon: Key },
  { id: 'bulk', label: 'Bulk Message', short: 'Bulk', icon: Send },
  { id: 'rules', label: 'Automation Rules', short: 'Rules', icon: GitBranch },
  { id: 'templates', label: 'Templates', short: 'Templates', icon: FileText },
  { id: 'ai', label: 'AI Memory', short: 'AI', icon: Brain },
];

const TRIGGER_TYPES = [
  { value: 'lead_disposition', label: 'Lead Disposition' },
  { value: 'app_enquiry', label: 'App Enquiry' },
  { value: 'service_status', label: 'Service Status' },
  { value: 'keyword', label: 'Keyword Match' },
  { value: 'inbound_message', label: 'Any Inbound Message' },
];

const TRIGGER_VALUES = {
  lead_disposition: ['callback', 'confirmed', 'not_interested', 'not_answer', 'answered', 'converted', 'interested', '*'],
  app_enquiry: ['car_loan', 'car_service', 'rsa', '*'],
  service_status: ['registered', 'assigned', 'resolved', 'closed', '*'],
  keyword: ['help', 'price', 'membership', 'callback'],
  inbound_message: ['all', '*'],
};

const CUSTOMER_SEGMENTS = [
  { value: 'all', label: 'All customers (with phone)' },
  { value: 'active_membership', label: 'Active membership' },
  { value: 'pending_membership', label: 'Pending membership' },
  { value: 'expiring_soon', label: 'Expiring in next 30 days' },
  { value: 'expiring_60_days', label: 'Expiring in 31–60 days' },
  { value: 'expiring_90_days', label: 'Expiring in 61–90 days' },
  { value: 'expiring_180_days', label: 'Expiring in 91–180 days' },
  { value: 'expiring_365_days', label: 'Expiring in 181–365 days' },
  { value: 'expired_membership', label: 'Expired membership' },
  { value: 'app_installed', label: 'App installed' },
  { value: 'no_app', label: 'No app installed' },
  { value: 'no_membership', label: 'No membership' },
  { value: 'has_vehicle', label: 'Has vehicle' },
  { value: 'no_vehicle', label: 'No vehicle' },
  { value: 'whatsapp_contacted', label: 'WhatsApp contacted' },
  { value: 'ever_responded', label: 'Ever replied on WA' },
  { value: 'never_responded', label: 'Never replied on WA' },
];

const INDIAN_STATES = [
  '', 'Delhi', 'Maharashtra', 'Karnataka', 'Uttar Pradesh', 'Haryana', 'Punjab',
  'Rajasthan', 'Gujarat', 'West Bengal', 'Tamil Nadu', 'Telangana', 'Madhya Pradesh',
  'Bihar', 'Kerala', 'Odisha', 'Assam', 'Jharkhand', 'Chhattisgarh', 'Uttarakhand',
];

const RETARGET_FILTERS = [
  { value: 'no_response', label: 'No response' },
  { value: 'failed', label: 'Failed delivery' },
  { value: 'not_delivered', label: 'Not delivered' },
  { value: 'not_read', label: 'Not read' },
  { value: 'no_click', label: 'Link not clicked' },
  { value: 'all_sent', label: 'All sent' },
];

const BULK_SOURCES = [
  { value: 'customers', label: 'Our Customers' },
  { value: 'csv', label: 'CSV Upload' },
  { value: 'manual', label: 'Manual Numbers' },
];

const SENDER_MODES = [
  { value: 'company_default', label: 'Default automation account' },
  { value: 'specific_account', label: 'Pick automation account' },
];

const fmtPctWithCount = (pct, count) => `${pct ?? 0}% (${Number(count ?? 0)})`;

const fmtSegmentOption = (seg, counts = {}) => {
  const n = Number(counts[seg.value] ?? 0);
  return `${seg.label} (${n})`;
};

const segmentCardTone = (value) => {
  if (value === 'active_membership') return 'emerald';
  if (value === 'expired_membership') return 'purple';
  if (value.startsWith('expiring')) return 'amber';
  return 'blue';
};

const buildFailureInsights = (detail) => {
  const fromApi = Array.isArray(detail?.failure_stats) ? detail.failure_stats : [];
  const failedRecipients = Array.isArray(detail?.failed_recipients)
    ? detail.failed_recipients
    : (detail?.recipients || []).filter((r) => r.status === 'failed' || r.delivery_status === 'failed');

  if (fromApi.length > 0) {
    return {
      stats: fromApi.map((row) => ({
        reason: row.reason,
        count: row.count,
        pct: row.pct,
        retryable: row.retryable === true,
        retry_hint: row.retry_hint || '',
      })),
      failedRecipients,
    };
  }

  const byReason = new Map();
  failedRecipients.forEach((r) => {
    const reason = (r.error || 'Unknown error').trim() || 'Unknown error';
    byReason.set(reason, (byReason.get(reason) || 0) + 1);
  });

  const total = Number(detail?.stats?.total_recipients || detail?.campaign?.total_recipients || 0);
  const stats = [...byReason.entries()]
    .map(([reason, count]) => ({
      reason,
      count,
      pct: total > 0 ? Math.round((1000 * count) / total) / 10 : 0,
    }))
    .sort((a, b) => b.count - a.count);

  return { stats, failedRecipients };
};

const fmtCampaignPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  if (digits.length === 10) return `+91${digits}`;
  return digits ? `+${digits}` : '—';
};

const emptyAccount = () => ({
  name: 'RoadServe Automation',
  phone_number_id: '', waba_id: '', permanent_token: '',
  verify_token: '', display_phone: '',
  is_default: false, is_active: true, ai_enabled: false,
});

const accountLabelClass = 'mb-1.5 block text-xs font-semibold text-gray-600';
const accountInputClass = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20';
const accountInputMonoClass = `${accountInputClass} font-mono text-[13px]`;

const emptyRule = () => ({
  name: '', trigger_type: 'lead_disposition', trigger_value: 'callback',
  sender_mode: 'company_default', account_id: '', message_mode: 'text',
  template_name: '', template_language: 'en', template_params: '[]',
  text_body: '', delay_minutes: 0, only_if_unassigned: false, enable_ai_reply: false,
});

const emptyTemplate = () => ({
  account_scope: 'all_accounts',
  account_id: '',
  name: '',
  category: 'UTILITY',
  language: 'en',
  header_type: 'NONE',
  header_text: '',
  header_text_examples: [''],
  header_media_name: '',
  header_media_mime: '',
  header_media_base64: '',
  body_text: '',
  body_examples: ['', ''],
  footer_text: '',
  buttons: [],
  /** When set, form is in edit mode (Meta template id) */
  editing_template_id: '',
  editing_status: '',
});

const TEMPLATE_CATEGORIES = [
  { value: 'UTILITY', label: 'Utility — order updates, alerts' },
  { value: 'MARKETING', label: 'Marketing — offers, promotions' },
  { value: 'AUTHENTICATION', label: 'Authentication — OTP / verify' },
];

const TEMPLATE_LANGUAGES = [
  { value: 'en', label: 'English (en)' },
  { value: 'en_US', label: 'English US (en_US)' },
  { value: 'en_GB', label: 'English UK (en_GB)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'hi_IN', label: 'Hindi IN (hi_IN)' },
  { value: 'mr', label: 'Marathi (mr)' },
  { value: 'ta', label: 'Tamil (ta)' },
  { value: 'te', label: 'Telugu (te)' },
  { value: 'bn', label: 'Bengali (bn)' },
  { value: 'gu', label: 'Gujarati (gu)' },
  { value: 'kn', label: 'Kannada (kn)' },
  { value: 'ml', label: 'Malayalam (ml)' },
  { value: 'pa', label: 'Punjabi (pa)' },
];

const HEADER_TYPES = [
  { value: 'NONE', label: 'No header' },
  { value: 'TEXT', label: 'Text' },
  { value: 'IMAGE', label: 'Image (JPG/PNG)' },
  { value: 'VIDEO', label: 'Video (MP4)' },
  { value: 'DOCUMENT', label: 'Document (PDF)' },
];

const countPlaceholders = (text = '') => {
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  if (!matches.length) return 0;
  return Math.max(...matches.map((m) => Number(m.replace(/\D/g, ''))));
};

const emptyBulk = () => ({
  name: '', account_id: '', phones: '', message_mode: 'text',
  text_body: '', template_name: '', template_language: 'en', template_params: '',
  template_header_type: 'NONE', template_header_image_url: '',
});

const StatCard = ({ label, value, sub, tone = 'emerald' }) => {
  const toneClass = tone === 'blue' ? 'text-blue-700' : tone === 'amber' ? 'text-amber-700' : tone === 'purple' ? 'text-purple-700' : 'text-emerald-700';
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${toneClass}`}>{value ?? 0}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
};

const qualityTone = (rating) => {
  const key = String(rating || '').toUpperCase();
  if (key === 'GREEN') return 'text-emerald-600';
  if (key === 'YELLOW') return 'text-amber-600';
  if (key === 'RED') return 'text-red-600';
  return 'text-gray-500';
};

const statusTone = (status) => {
  const key = String(status || '').toUpperCase();
  if (key === 'CONNECTED') return 'text-emerald-600';
  if (key === 'DISCONNECTED' || key === 'BANNED') return 'text-red-600';
  return 'text-amber-600';
};

const AccountMetric = ({ icon: Icon, label, value, valueClass = 'text-blue-700', uppercase = true }) => (
  <div className="px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
      <Icon size={13} className="shrink-0" />
      <span className="truncate">{label}</span>
    </div>
    <p className={`mt-1 truncate text-xs font-bold ${uppercase ? 'uppercase' : ''} ${valueClass}`}>{value || '—'}</p>
  </div>
);

const AutomationAccountCard = ({
  account,
  details,
  loading,
  error,
  onEdit,
  onTest,
  onRefresh,
  onDelete,
  testing,
  deleting,
}) => {
  const phone = details?.display_phone_number || account.display_phone || account.phone_number_id;
  const title = details?.verified_name || account.name;
  const isConnected = String(details?.status || '').toUpperCase() === 'CONNECTED';

  const copyPhone = () => {
    if (!phone) return;
    navigator.clipboard.writeText(phone.replace(/\s+/g, ''));
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50">
            <MessageSquare size={18} className="text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
              <span className={`h-2 w-2 shrink-0 rounded-full ${isConnected ? 'bg-emerald-500' : loading ? 'bg-gray-300 animate-pulse' : 'bg-amber-500'}`} />
              {account.is_default && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-600/15">
                  Default
                </span>
              )}
              {account.ai_enabled && (
                <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700 ring-1 ring-purple-600/15">
                  AI
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <p className="truncate text-sm text-gray-600 tabular-nums">{phone}</p>
              {phone && (
                <button
                  type="button"
                  onClick={copyPhone}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  title="Copy phone number"
                >
                  <Copy size={13} />
                </button>
              )}
            </div>
            <p className="mt-1 truncate text-[11px] text-gray-400">
              {account.name !== title ? `${account.name} · ` : ''}
              Token: {account.token_masked || '—'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-slate-50/80 px-4 py-2">
        <button
          type="button"
          onClick={() => onRefresh(account.id)}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
        <button
          type="button"
          onClick={() => onTest(account.id)}
          disabled={testing}
          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        >
          {testing ? <Loader2 size={13} className="animate-spin" /> : <TestTube2 size={13} />}
          Test
        </button>
        <button
          type="button"
          onClick={() => onEdit(account)}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <Pencil size={13} />
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(account)}
          disabled={deleting}
          className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
        >
          {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
          Delete
        </button>
      </div>

      {loading && !details ? (
        <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin text-emerald-600" />
          Meta se account details load ho rahi hain...
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 border-b border-gray-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Details load nahi hui</p>
            <p className="text-xs text-amber-700">{error}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-px border-b border-gray-100 bg-gray-100 sm:grid-cols-4">
          <div className="bg-white">
            <AccountMetric
              icon={Wifi}
              label="Number Status"
              value={details?.status || 'UNKNOWN'}
              valueClass={statusTone(details?.status)}
            />
          </div>
          <div className="bg-white">
            <AccountMetric
              icon={Gauge}
              label="Quality Score"
              value={details?.quality_rating || 'UNKNOWN'}
              valueClass={qualityTone(details?.quality_rating)}
            />
          </div>
          <div className="bg-white">
            <AccountMetric
              icon={MessageSquare}
              label="Message Limit"
              value={details?.messaging_limit_label || '—'}
              valueClass="text-blue-700"
              uppercase={false}
            />
          </div>
          <div className="bg-white">
            <AccountMetric
              icon={KeyRound}
              label="2FA Enabled"
              value={details?.two_fa_enabled ? 'Yes' : 'No'}
              valueClass={details?.two_fa_enabled ? 'text-emerald-600' : 'text-gray-500'}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-[10px] text-gray-400">
        <span className="truncate">Phone ID: {account.phone_number_id}</span>
        <span className="truncate">WABA: {account.waba_id}</span>
        {details?.fetched_at && (
          <span className="ml-auto truncate">Updated {new Date(details.fetched_at).toLocaleString('en-IN')}</span>
        )}
      </div>
    </div>
  );
};

export default function WhatsAppAutomation() {
  const [section, setSection] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [accountDetailsMap, setAccountDetailsMap] = useState({});
  const [accountDetailsLoading, setAccountDetailsLoading] = useState({});
  const [accountDetailsError, setAccountDetailsError] = useState({});
  const [testingAccountId, setTestingAccountId] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deletingAccountId, setDeletingAccountId] = useState(null);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [setupRequired, setSetupRequired] = useState(false);
  const [accountForm, setAccountForm] = useState(emptyAccount());
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [showAddAccountForm, setShowAddAccountForm] = useState(false);
  const [importingEnv, setImportingEnv] = useState(false);
  const [showPermanentToken, setShowPermanentToken] = useState(false);

  const [rules, setRules] = useState([]);
  const [ruleForm, setRuleForm] = useState(emptyRule());
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [logs, setLogs] = useState([]);

  const [templates, setTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState(emptyTemplate());
  const [templateSubmitting, setTemplateSubmitting] = useState(false);
  const [aiTemplateBrief, setAiTemplateBrief] = useState('');
  const [aiTemplateGenerating, setAiTemplateGenerating] = useState(false);
  const [syncAccountId, setSyncAccountId] = useState('');
  const [templateResults, setTemplateResults] = useState([]);
  const [templatesSyncing, setTemplatesSyncing] = useState(false);
  const [highlightTemplateName, setHighlightTemplateName] = useState('');
  const [templateStatusFilter, setTemplateStatusFilter] = useState('all');
  const [templateSubTab, setTemplateSubTab] = useState('library');
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('');

  const [bulkForm, setBulkForm] = useState(emptyBulk());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSource, setBulkSource] = useState('customers');
  const [bulkRecipients, setBulkRecipients] = useState([]);
  const [bulkCustomerSegment, setBulkCustomerSegment] = useState('all');
  const [bulkCustomerSearch, setBulkCustomerSearch] = useState('');
  const [bulkCustomerState, setBulkCustomerState] = useState('');
  const [bulkCustomersLoading, setBulkCustomersLoading] = useState(false);
  const [bulkAudienceRefreshing, setBulkAudienceRefreshing] = useState(false);
  const [bulkAudienceCache, setBulkAudienceCache] = useState(null);
  const [bulkPreviewTotal, setBulkPreviewTotal] = useState(0);
  const [bulkCsvErrors, setBulkCsvErrors] = useState([]);
  const [bulkTrackLinks, setBulkTrackLinks] = useState(true);
  const [bulkSubTab, setBulkSubTab] = useState('send');
  const [bulkCampaigns, setBulkCampaigns] = useState([]);
  const [bulkCampaignsLoading, setBulkCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [retargetFromId, setRetargetFromId] = useState(null);

  const [aiMemory, setAiMemory] = useState(null);
  const [aiTestMsg, setAiTestMsg] = useState('');
  const [aiTestReply, setAiTestReply] = useState('');

  const flash = (msg, isErr = false) => {
    if (isErr) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 5000);
  };

  const loadAccounts = useCallback(async () => {
    try {
      const data = await WhatsAppAutomationService.listAccounts();
      setAccounts(data);
      return data;
    } catch (e) {
      setAccounts([]);
      flash(e?.message || 'Failed to load accounts', true);
      return [];
    }
  }, []);

  const refreshAccountDetails = useCallback(async (accountId) => {
    if (!accountId) return null;
    setAccountDetailsLoading((prev) => ({ ...prev, [accountId]: true }));
    setAccountDetailsError((prev) => ({ ...prev, [accountId]: '' }));
    try {
      const details = await WhatsAppAutomationService.getAccountDetails(accountId);
      setAccountDetailsMap((prev) => ({ ...prev, [accountId]: details }));
      return details;
    } catch (e) {
      setAccountDetailsError((prev) => ({ ...prev, [accountId]: e?.message || 'Failed to load details' }));
      return null;
    } finally {
      setAccountDetailsLoading((prev) => ({ ...prev, [accountId]: false }));
    }
  }, []);

  const refreshAllAccountDetails = useCallback(async (accountRows) => {
    if (!Array.isArray(accountRows) || accountRows.length === 0) return;
    await Promise.allSettled(accountRows.map((acc) => refreshAccountDetails(acc.id)));
  }, [refreshAccountDetails]);

  const fetchedAccountDetailsKeyRef = useRef('');

  const loadStats = useCallback(async () => {
    try {
      const data = await WhatsAppAutomationService.getStats();
      setStats(data || EMPTY_STATS);
      setSetupRequired(!!data?._setup_required);
      if (data?.audience_cache) setBulkAudienceCache(data.audience_cache);
      if (data?._segments_pending) {
        flash('Customer segment counts are still loading — refresh audience cache if counts show 0', true);
      }
    } catch (e) {
      const msg = String(e?.message || '');
      const isTimeout = msg.includes('statement timeout') || msg.includes('canceling statement');
      const isMissing = msg.includes('does not exist') || msg.includes('Could not find');
      setStats(EMPTY_STATS);
      setSetupRequired(isMissing && !isTimeout);
      flash(
        isTimeout
          ? 'Stats timed out — run whatsapp-bulk-audience-cache.sql then SELECT whatsapp_bulk_refresh_audience();'
          : (e?.message || 'Stats load failed'),
        true,
      );
    }
  }, []);

  const loadRules = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([
        WhatsAppAutomationService.listRules(),
        WhatsAppAutomationService.listLogs(50),
      ]);
      setRules(r);
      setLogs(l);
    } catch (e) {
      setRules([]);
      setLogs([]);
    }
  }, []);

  const loadAi = useCallback(async () => {
    try {
      const data = await WhatsAppAutomationService.getAiMemory();
      const base = data || { system_prompt: '', global_enabled: false };
      // Auto-upgrade outdated Basic/Pro/Elite prompt from marketing-site knowledge
      if (isLegacyAiPrompt(base.system_prompt)) {
        const next = { ...base, system_prompt: ROADSERVE_WHATSAPP_AI_PROMPT };
        setAiMemory(next);
        try {
          await WhatsAppAutomationService.saveAiMemory(next);
        } catch {
          // keep UI updated even if save fails
        }
        return;
      }
      setAiMemory(base);
    } catch {
      setAiMemory({ system_prompt: ROADSERVE_WHATSAPP_AI_PROMPT, global_enabled: false });
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    await Promise.allSettled([loadAccounts(), loadStats(), loadRules(), loadAi()]);
    setLoading(false);
  }, [loadAccounts, loadStats, loadRules, loadAi]);

  const loadBulkCustomers = useCallback(async () => {
    setBulkCustomersLoading(true);
    try {
      // Preview only (100). Full send uses segment_queue from audience cache — no 1L browser load.
      const data = await WhatsAppAutomationService.listBulkCustomers({
        segment: bulkCustomerSegment,
        search: bulkCustomerSearch,
        state: bulkCustomerState,
        limit: 100,
        offset: 0,
      });
      if (data._setup_required) {
        flash('Run whatsapp-bulk-audience-cache.sql in Supabase, then Refresh audience cache', true);
        setBulkRecipients([]);
        setBulkPreviewTotal(0);
        return;
      }
      setBulkRecipients(data.items || []);
      setBulkPreviewTotal(Number(data.total || 0));
      if (data.cache) setBulkAudienceCache(data.cache);
      if (data.cache?.empty) {
        flash('Audience cache empty — click Refresh audience cache once', true);
      } else {
        flash(`Preview ${(data.items || []).length} of ${Number(data.total || 0).toLocaleString('en-IN')} (send queues all from DB)`);
      }
    } catch (e) {
      setBulkRecipients([]);
      setBulkPreviewTotal(0);
      flash(e.message || 'Failed to load customers', true);
    } finally {
      setBulkCustomersLoading(false);
    }
  }, [bulkCustomerSegment, bulkCustomerSearch, bulkCustomerState]);

  const refreshBulkAudienceCache = useCallback(async () => {
    setBulkAudienceRefreshing(true);
    try {
      const result = await WhatsAppAutomationService.refreshBulkAudience();
      if (result._setup_required) {
        flash(result.error || 'Run whatsapp-bulk-audience-cache.sql in Supabase first', true);
        return;
      }
      const status = await WhatsAppAutomationService.getBulkAudienceStatus();
      setBulkAudienceCache(status);
      flash(`Audience cache ready: ${Number(result.row_count || status.row_count || 0).toLocaleString('en-IN')} phones`);
      await loadStats();
      await loadBulkCustomers();
    } catch (e) {
      flash(
        `${e.message || 'Refresh failed'}. Agar gateway timeout ho to Supabase SQL Editor mein chalao: SELECT whatsapp_bulk_refresh_audience();`,
        true,
      );
    } finally {
      setBulkAudienceRefreshing(false);
    }
  }, [loadBulkCustomers, loadStats]);

  const loadBulkCampaigns = useCallback(async ({ syncMeta = false } = {}) => {
    setBulkCampaignsLoading(true);
    try {
      // Fast path: show list immediately (don't block on Meta Graph API)
      const data = await WhatsAppAutomationService.listBulkCampaigns(100);
      setBulkCampaigns(data || []);

      if (!syncMeta) return;

      const templateIds = (data || [])
        .filter((c) => c.message_mode === 'template' && c.template_name)
        .slice(0, 5)
        .map((c) => c.id);
      if (!templateIds.length) return;

      // Background: Meta URL button + short-link click sync
      WhatsAppAutomationService.syncBulkCampaignMetaUrlClicks(templateIds)
        .catch((syncErr) => console.warn('[url clicks sync]', syncErr?.message || syncErr))
        .then(() => WhatsAppAutomationService.listBulkCampaigns(100))
        .then((fresh) => WhatsAppAutomationService.enrichBulkCampaignUrlClicks(fresh || []))
        .then((fresh) => setBulkCampaigns(fresh || []))
        .catch((syncErr) => console.warn('[url clicks sync]', syncErr?.message || syncErr));
    } catch (e) {
      setBulkCampaigns([]);
      flash(e?.message || 'Failed to load campaigns', true);
    } finally {
      setBulkCampaignsLoading(false);
    }
  }, []);

  const openCampaignDetail = async (campaignId) => {
    try {
      setSelectedCampaign(campaignId);
      setBulkSubTab('campaigns');
      // Show detail first; Meta URL clicks sync in background
      const data = await WhatsAppAutomationService.getBulkCampaignDetail(campaignId);
      setCampaignDetail(data);
      WhatsAppAutomationService.syncBulkCampaignMetaUrlClicks([campaignId])
        .catch(() => {})
        .then(() => WhatsAppAutomationService.getBulkCampaignDetail(campaignId))
        .then(async (detail) => {
          if (!detail?.campaign) return detail;
          const [enriched] = await WhatsAppAutomationService.enrichBulkCampaignUrlClicks([{
            ...detail.campaign,
            sent_count: detail.stats?.sent_count,
            meta_url_click_unique: detail.stats?.meta_url_click_unique,
            meta_url_click_count: detail.stats?.meta_url_click_count,
            link_click_count: detail.stats?.link_click_count,
          }]);
          if (!enriched) return detail;
          return {
            ...detail,
            stats: {
              ...detail.stats,
              short_link_click_count: enriched.short_link_click_count,
              link_click_count: enriched.link_click_count,
              link_click_pct: enriched.link_click_pct,
            },
          };
        })
        .then(setCampaignDetail)
        .catch(() => {});
    } catch (e) {
      flash(e.message, true);
    }
  };

  const handleResumeBulkCampaign = async (campaignId, campaignName) => {
    const label = campaignName ? `"${campaignName}"` : 'Is campaign';
    if (!window.confirm(`${label} resume karna hai? Jo stop pe skip hue the woh dubara queue me jayenge.`)) return;
    try {
      const res = await WhatsAppAutomationService.resumeBulkCampaign(campaignId);
      if (res?.ok === false) {
        flash(res.error || 'Resume failed', true);
        return;
      }
      if (res?.already_running) {
        flash('Already sending — Resume dobara mat dabao (rate-limit ho jata hai)');
      } else if (res?.from_stopped && res?.requeued_pending > 0) {
        flash(`Campaign resumed — ${res.requeued_pending} pending messages queue me`);
      } else if (res?.from_stopped) {
        flash('Campaign resumed — sending continues');
      } else {
        flash('Sending continues (ek hi Resume kaafi hai)');
      }
      loadBulkCampaigns({ syncMeta: false });
      if (selectedCampaign === campaignId) openCampaignDetail(campaignId);
    } catch (e) {
      flash(e.message || 'Resume failed', true);
    }
  };

  const handleRetryFailedReason = async (row) => {
    if (!selectedCampaign || !row?.reason) return;
    const force = !row.retryable;
    const msg = row.retryable
      ? `${row.count} failed messages is reason se retry queue me dalne hain?`
      : `${row.count} messages — ${row.retry_hint || 'usually permanent error'}. Phir bhi retry?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await WhatsAppAutomationService.requeueFailedByReason(
        selectedCampaign,
        row.reason,
        { force },
      );
      const n = Number(res?.requeued || 0);
      const blocked = Number(res?.blocked_non_retryable || 0);
      if (n > 0) {
        flash(`${n} messages retry queue me — sending shuru`);
      } else if (blocked > 0 && !force) {
        flash(`${blocked} non-retryable — "Retry anyway" use karo ya pehle fix karo`, true);
      } else {
        flash('Koi message requeue nahi hua', true);
      }
      loadBulkCampaigns({ syncMeta: false });
      openCampaignDetail(selectedCampaign);
    } catch (e) {
      flash(e.message || 'Retry failed', true);
    }
  };

  const handleRetryAllRetryableFailures = async () => {
    if (!selectedCampaign) return;
    if (!window.confirm('Sirf safe/retryable failures (rate-limit, 131053 HTTP 500, etc.) dubara queue me jayenge. Continue?')) return;
    try {
      const res = await WhatsAppAutomationService.requeueAllRetryableFailures(selectedCampaign);
      const n = Number(res?.requeued || 0);
      flash(n > 0 ? `${n} retryable failures queue me — sending shuru` : 'Koi retryable failure nahi mila', n > 0 ? false : true);
      loadBulkCampaigns({ syncMeta: false });
      openCampaignDetail(selectedCampaign);
    } catch (e) {
      flash(e.message || 'Retry failed', true);
    }
  };

  const loadRetarget = async (campaignId, filter) => {
    try {
      const data = await WhatsAppAutomationService.getRetargetRecipients(campaignId, filter);
      setBulkRecipients(data.items || []);
      setRetargetFromId(campaignId);
      setBulkSource('customers');
      setBulkSubTab('send');
      setBulkForm((f) => ({
        ...f,
        name: `${f.name || 'Retarget'} — ${RETARGET_FILTERS.find((x) => x.value === filter)?.label || filter}`,
      }));
      flash(`Retarget loaded: ${data.total} customers (${filter})`);
    } catch (e) {
      flash(e.message, true);
    }
  };

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (section === 'dashboard') loadStats();
  }, [section, loadStats]);
  useEffect(() => {
    if (section === 'bulk' && bulkSource === 'customers') loadStats();
  }, [section, bulkSource, loadStats]);

  useEffect(() => {
    if (section !== 'accounts' || accounts.length === 0) return;
    const key = accounts.map((acc) => acc.id).sort().join(',');
    if (fetchedAccountDetailsKeyRef.current === key) return;
    fetchedAccountDetailsKeyRef.current = key;
    refreshAllAccountDetails(accounts);
  }, [section, accounts, refreshAllAccountDetails]);

  useEffect(() => {
    if (section !== 'bulk' || bulkSource !== 'customers' || bulkSubTab !== 'send') return undefined;
    const timer = setTimeout(() => {
      loadBulkCustomers();
    }, 350);
    return () => clearTimeout(timer);
  }, [section, bulkSource, bulkSubTab, bulkCustomerSegment, bulkCustomerState, bulkCustomerSearch, loadBulkCustomers]);

  useEffect(() => {
    if (section === 'bulk' && bulkSubTab === 'campaigns') {
      // First open: list fast + one background Meta sync
      loadBulkCampaigns({ syncMeta: true });
    }
    if (section === 'templates') {
      syncTemplates({ silent: true });
    }
    if (section === 'bulk' && bulkSubTab === 'send') {
      syncTemplates({ silent: true, accountId: bulkForm.account_id || null });
    }
  }, [section, bulkSubTab, loadBulkCampaigns, bulkForm.account_id]);

  useEffect(() => {
    if (section !== 'bulk' || bulkSubTab !== 'campaigns') return undefined;
    // Poll only DB stats — never Meta Graph (slow)
    const interval = setInterval(() => {
      loadBulkCampaigns({ syncMeta: false });
      if (selectedCampaign) {
        WhatsAppAutomationService.getBulkCampaignDetail(selectedCampaign)
          .then(setCampaignDetail)
          .catch(() => {});
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [section, bulkSubTab, selectedCampaign, loadBulkCampaigns]);

  const saveAccount = async () => {
    try {
      if (!accountForm.name || !accountForm.phone_number_id || !accountForm.waba_id) {
        flash('Name, Phone Number ID and WABA ID required', true);
        return;
      }
      if (!editingAccountId && !accountForm.permanent_token) {
        flash('Permanent token required for new account', true);
        return;
      }
      const savedPhoneNumberId = accountForm.phone_number_id;
      const saved = await WhatsAppAutomationService.saveAccount({ ...accountForm, id: editingAccountId });
      flash('Automation account saved');
      const savedId = editingAccountId || saved?.id;
      setAccountForm(emptyAccount());
      setEditingAccountId(null);
      setShowAddAccountForm(false);
      const rows = await loadAccounts();
      loadStats();
      const targetId = savedId || rows.find(
        (acc) => acc.phone_number_id === savedPhoneNumberId,
      )?.id;
      if (targetId) await refreshAccountDetails(targetId);
    } catch (e) {
      flash(e.message, true);
    }
  };

  const editAccount = (acc) => {
    setEditingAccountId(acc.id);
    setShowAddAccountForm(true);
    setAccountForm({
      name: acc.name,
      phone_number_id: acc.phone_number_id,
      waba_id: acc.waba_id,
      permanent_token: '',
      verify_token: acc.verify_token || '',
      display_phone: acc.display_phone || '',
      is_default: acc.is_default,
      is_active: acc.is_active,
      ai_enabled: acc.ai_enabled,
    });
  };

  const cancelAccountForm = () => {
    setEditingAccountId(null);
    setAccountForm(emptyAccount());
    setShowAddAccountForm(false);
  };

  const accountFormOpen = showAddAccountForm || !!editingAccountId || accounts.length === 0;

  const testAccount = async (id) => {
    setTestingAccountId(id);
    try {
      const res = await WhatsAppAutomationService.getAccountDetails(id);
      setAccountDetailsMap((prev) => ({ ...prev, [id]: res }));
      setAccountDetailsError((prev) => ({ ...prev, [id]: '' }));
      flash(`Connected: ${res.display_phone_number || res.verified_name || res.status || 'OK'}`);
    } catch (e) {
      setAccountDetailsError((prev) => ({ ...prev, [id]: e.message }));
      flash(e.message, true);
    } finally {
      setTestingAccountId(null);
    }
  };

  const requestDeleteAccount = (account) => {
    setAccountToDelete(account);
  };

  const cancelDeleteAccount = () => {
    if (deletingAccountId) return;
    setAccountToDelete(null);
  };

  const confirmDeleteAccount = async () => {
    if (!accountToDelete?.id) return;
    const deleteId = accountToDelete.id;
    setDeletingAccountId(deleteId);
    try {
      await WhatsAppAutomationService.deleteAccount(deleteId);
      if (editingAccountId === deleteId) {
        setEditingAccountId(null);
        setAccountForm(emptyAccount());
      }
      setAccountDetailsMap((prev) => {
        const next = { ...prev };
        delete next[deleteId];
        return next;
      });
      setAccountDetailsLoading((prev) => {
        const next = { ...prev };
        delete next[deleteId];
        return next;
      });
      setAccountDetailsError((prev) => {
        const next = { ...prev };
        delete next[deleteId];
        return next;
      });
      setAccountToDelete(null);
      await loadAccounts();
      loadStats();
      flash('Automation account deleted');
    } catch (e) {
      flash(e.message, true);
    } finally {
      setDeletingAccountId(null);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(WhatsAppAutomationService.getWebhookUrl());
    flash('Webhook URL copied');
  };

  const importFromSupabase = async () => {
    setImportingEnv(true);
    try {
      const creds = await WhatsAppAutomationService.getSupabaseEnvCredentials();
      setAccountForm((f) => ({
        ...f,
        phone_number_id: creds.phone_number_id || f.phone_number_id,
        waba_id: creds.waba_id || f.waba_id,
        permanent_token: creds.permanent_token || f.permanent_token,
        verify_token: creds.verify_token || f.verify_token,
        display_phone: creds.display_phone || f.display_phone,
        name: creds.verified_name ? `${creds.verified_name} (Supabase)` : f.name,
      }));
      flash(`Loaded from Supabase secrets (${creds.token_masked || 'token ok'})${creds.display_phone ? ` — ${creds.display_phone}` : ''}`);
    } catch (e) {
      flash(e.message || 'Import failed — deploy whatsapp-env-import edge function first', true);
    } finally {
      setImportingEnv(false);
    }
  };

  const saveRule = async () => {
    try {
      if (!ruleForm.name || !ruleForm.trigger_type || !ruleForm.trigger_value) {
        flash('Rule name and trigger required', true);
        return;
      }
      let templateParams = [];
      try { templateParams = JSON.parse(ruleForm.template_params || '[]'); } catch { templateParams = []; }

      await WhatsAppAutomationService.saveRule({
        ...ruleForm,
        id: editingRuleId,
        template_params: templateParams,
        account_id: ruleForm.sender_mode === 'specific_account' ? ruleForm.account_id : null,
      });
      flash('Rule saved');
      setRuleForm(emptyRule());
      setEditingRuleId(null);
      loadRules();
      loadStats();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const editRule = (rule) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      name: rule.name,
      trigger_type: rule.trigger_type,
      trigger_value: rule.trigger_value,
      sender_mode: rule.sender_mode === 'assigned_tc_tl' ? 'company_default' : (rule.sender_mode || 'company_default'),
      account_id: rule.account_id || '',
      message_mode: rule.message_mode || 'text',
      template_name: rule.template_name || '',
      template_language: rule.template_language || 'en',
      template_params: JSON.stringify(rule.template_params || []),
      text_body: rule.text_body || '',
      delay_minutes: rule.delay_minutes || 0,
      only_if_unassigned: rule.only_if_unassigned,
      enable_ai_reply: rule.enable_ai_reply,
    });
  };

  const syncTemplates = async ({ silent = false, accountId } = {}) => {
    setTemplatesSyncing(true);
    try {
      const json = await WhatsAppAutomationService.syncTemplates((accountId ?? syncAccountId) || null);
      if (!json.success) throw new Error(json.error || 'Sync failed');
      const list = json.templates || [];
      setTemplates(list);
      if (!silent) flash(`Synced ${list.length} templates from Meta`);
      return list;
    } catch (e) {
      if (!silent) flash(e.message, true);
      return [];
    } finally {
      setTemplatesSyncing(false);
    }
  };

  const filteredTemplates = templates.filter((t) => {
    if (templateStatusFilter === 'all') return true;
    return String(t.status || '').toLowerCase() === templateStatusFilter;
  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    const order = { PENDING: 0, IN_APPEAL: 1, REJECTED: 2, APPROVED: 3, PAUSED: 4, DISABLED: 5 };
    const ao = order[a.status] ?? 9;
    const bo = order[b.status] ?? 9;
    if (ao !== bo) return ao - bo;
    return String(a.name).localeCompare(String(b.name));
  });

  const templateKey = (t) => `${t.name}::${t.language}`;
  const selectedTemplate = sortedTemplates.find((t) => templateKey(t) === selectedTemplateKey)
    || sortedTemplates.find((t) => t.name === highlightTemplateName)
    || sortedTemplates[0]
    || null;

  const templateCounts = templates.reduce((acc, t) => {
    const s = String(t.status || '').toLowerCase();
    acc.all += 1;
    if (s === 'pending') acc.pending += 1;
    if (s === 'approved') acc.approved += 1;
    if (s === 'rejected') acc.rejected += 1;
    return acc;
  }, { all: 0, pending: 0, approved: 0, rejected: 0 });

  const approvedBulkTemplates = templates.filter((t) => String(t.status || '').toUpperCase() === 'APPROVED');

  const onBulkTemplatePick = (compositeKey) => {
    if (!compositeKey) {
      setBulkForm((f) => ({
        ...f,
        template_name: '',
        template_language: 'en',
        template_params: '[]',
        template_header_type: 'NONE',
        template_header_image_url: '',
      }));
      return;
    }
    const tpl = approvedBulkTemplates.find((t) => `${t.name}::${t.language}` === compositeKey);
    if (!tpl) return;
    const paramSuggestions = suggestTemplateParams(tpl);
    const rawHeaderUrl = String(tpl.header_image_url || '');
    const ephemeral = /scontent\.|fbcdn\.net|whatsapp\.net|fbsbx\.com|lookaside/i.test(rawHeaderUrl);
    const permanentGuess = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(String(tpl.header_type || '').toUpperCase())
      ? `${ENV.SUPABASE_URL}/storage/v1/object/public/roadserve-uploads/whatsapp-templates/${tpl.name}_header.jpg`
      : '';
    setBulkForm((f) => ({
      ...f,
      template_name: tpl.name,
      template_language: tpl.language || 'en',
      template_params: JSON.stringify(paramSuggestions),
      template_header_type: tpl.header_type || 'NONE',
      // Meta sample CDN URLs expire → prefer permanent Storage path
      template_header_image_url: ephemeral ? permanentGuess : (rawHeaderUrl || permanentGuess),
    }));
  };

  useEffect(() => {
    if (section === 'templates' && sortedTemplates.length > 0 && !selectedTemplateKey) {
      setSelectedTemplateKey(templateKey(sortedTemplates[0]));
    }
  }, [section, sortedTemplates, selectedTemplateKey]);

  const updateBodyExamples = (bodyText, prevExamples = []) => {
    const n = countPlaceholders(bodyText);
    return Array.from({ length: Math.max(n, 1) }, (_, i) => prevExamples[i] || '');
  };

  const setTemplateBody = (bodyText) => {
    setTemplateForm((f) => ({
      ...f,
      body_text: bodyText,
      body_examples: updateBodyExamples(bodyText, f.body_examples),
    }));
  };

  const addTemplateButton = (type) => {
    setTemplateForm((f) => {
      const hasQuick = f.buttons.some((b) => b.type === 'QUICK_REPLY');
      const hasCta = f.buttons.some((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
      if (type === 'QUICK_REPLY' && hasCta) {
        flash('Remove CTA buttons before adding quick replies', true);
        return f;
      }
      if (type !== 'QUICK_REPLY' && hasQuick) {
        flash('Remove quick replies before adding CTA buttons', true);
        return f;
      }
      if (type === 'QUICK_REPLY' && f.buttons.length >= 3) {
        flash('Max 3 quick reply buttons', true);
        return f;
      }
      if (type !== 'QUICK_REPLY' && f.buttons.length >= 2) {
        flash('Max 2 CTA buttons (URL / Phone)', true);
        return f;
      }
      return {
        ...f,
        buttons: [...f.buttons, { type, text: '', url: '', url_examples: [''], phone_number: '' }],
      };
    });
  };

  const updateTemplateButton = (idx, patch) => {
    setTemplateForm((f) => ({
      ...f,
      buttons: f.buttons.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));
  };

  const removeTemplateButton = (idx) => {
    setTemplateForm((f) => ({ ...f, buttons: f.buttons.filter((_, i) => i !== idx) }));
  };

  const onHeaderMediaPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxMb = templateForm.header_type === 'VIDEO' ? 15 : 5;
    if (file.size > maxMb * 1024 * 1024) {
      flash(`File too large — max ${maxMb}MB for sample upload`, true);
      e.target.value = '';
      return;
    }
    const name = String(file.name || '').toLowerCase();
    let mime = String(file.type || '').toLowerCase();
    if (mime === 'image/jpg' || mime === 'image/pjpeg') mime = 'image/jpeg';
    if (!mime) {
      if (name.endsWith('.png')) mime = 'image/png';
      else if (name.endsWith('.jpg') || name.endsWith('.jpeg')) mime = 'image/jpeg';
      else if (name.endsWith('.mp4')) mime = 'video/mp4';
      else if (name.endsWith('.pdf')) mime = 'application/pdf';
    }
    const allowed = templateForm.header_type === 'VIDEO'
      ? ['video/mp4']
      : templateForm.header_type === 'DOCUMENT'
        ? ['application/pdf']
        : ['image/jpeg', 'image/png'];
    if (!allowed.includes(mime)) {
      flash(`Meta sirf ${allowed.join(' / ')} allow karta hai — WEBP/HEIC mat use karo`, true);
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setTemplateForm((f) => ({
        ...f,
        header_media_name: file.name,
        header_media_mime: mime,
        header_media_base64: String(reader.result || ''),
      }));
      flash(`Sample file ready: ${file.name} (${mime})`);
    };
    reader.readAsDataURL(file);
  };

  const aiBriefPlaceholder = () => {
    if (templateForm.category === 'MARKETING') {
      return 'e.g. Expired RSA members ko renewal offer — 20% off, link to membership page';
    }
    if (templateForm.category === 'AUTHENTICATION') {
      return 'e.g. Customer app login / phone verify OTP message';
    }
    return 'e.g. Physical RSA card courier shipped — AWB number aur expected delivery batana hai';
  };

  const generateTemplateWithAi = async () => {
    if (!aiTemplateBrief.trim()) {
      flash('Pehle batao template kis baare mein hai (topic / use case)', true);
      return;
    }
    setAiTemplateGenerating(true);
    try {
      const draft = await WhatsAppAutomationService.generateTemplateWithAi({
        category: templateForm.category,
        language: templateForm.language,
        brief: aiTemplateBrief,
        templateName: templateForm.name,
      });

      const bodyText = String(draft.body_text || '').trim();
      const examples = Array.isArray(draft.body_examples) ? draft.body_examples : [];
      const varCount = countPlaceholders(bodyText);

      setTemplateForm((f) => ({
        ...f,
        name: draft.name || f.name,
        header_type: ['TEXT', 'NONE'].includes(draft.header_type) ? draft.header_type : 'NONE',
        header_text: draft.header_text || '',
        header_text_examples: draft.header_text ? [draft.header_text_examples?.[0] || 'Sample'] : [''],
        body_text: bodyText,
        body_examples: Array.from({ length: Math.max(varCount, 1) }, (_, i) => examples[i] || `Sample${i + 1}`),
        footer_text: draft.footer_text || '',
        buttons: Array.isArray(draft.buttons)
          ? draft.buttons.filter((b) => b?.text?.trim()).map((b) => ({
            type: b.type || 'URL',
            text: b.text || '',
            url: b.url || '',
            url_examples: b.url_examples || [''],
            phone_number: b.phone_number || '',
          }))
          : [],
      }));
      flash('AI template draft ready — review & edit before Meta submit');
    } catch (e) {
      flash(e.message || 'AI generation failed', true);
    } finally {
      setAiTemplateGenerating(false);
    }
  };

  const isEditingTemplate = !!templateForm.editing_template_id;

  const templateFormFromMeta = (tpl, { name, editingId = '', editingStatus = '' } = {}) => {
    const bodyExamples = Array.isArray(tpl.body_examples) && tpl.body_examples.length
      ? [...tpl.body_examples]
      : ['', ''];
    while (bodyExamples.length < 2) bodyExamples.push('');
    return {
      ...emptyTemplate(),
      account_scope: 'single',
      account_id: syncAccountId || accounts.find((a) => a.is_default)?.id || accounts[0]?.id || '',
      name: name || tpl.name || '',
      category: String(tpl.category || 'UTILITY').toUpperCase(),
      language: tpl.language || 'en',
      header_type: tpl.header_type || 'NONE',
      header_text: tpl.header_text || '',
      header_text_examples: Array.isArray(tpl.header_text_examples) && tpl.header_text_examples.length
        ? tpl.header_text_examples
        : [''],
      body_text: tpl.body_text || '',
      body_examples: bodyExamples,
      footer_text: tpl.footer_text || '',
      buttons: Array.isArray(tpl.buttons)
        ? tpl.buttons.map((b) => ({
          type: b.type || 'QUICK_REPLY',
          text: b.text || '',
          url: b.url || '',
          url_examples: b.url_examples || [''],
          phone_number: b.phone_number || '',
        }))
        : [],
      editing_template_id: editingId,
      editing_status: editingStatus,
    };
  };

  const nextCloneTemplateName = (baseName) => {
    const root = String(baseName || 'template')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_|_$/g, '')
      .replace(/_v\d+$/, '')
      .replace(/_copy\d*$/, '') || 'template';
    const taken = new Set(
      templates.map((t) => String(t.name || '').toLowerCase()),
    );
    let candidate = `${root}_v2`;
    let n = 2;
    while (taken.has(candidate)) {
      n += 1;
      candidate = `${root}_v${n}`;
    }
    return candidate.slice(0, 512);
  };

  const startEditTemplate = (tpl) => {
    if (!tpl) return;
    const status = String(tpl.status || '').toUpperCase();
    if (!['APPROVED', 'REJECTED', 'PAUSED'].includes(status)) {
      flash('Sirf Approved / Rejected / Paused templates edit ho sakti hain', true);
      return;
    }
    if (!tpl.id) {
      flash('Template id missing — pehle Sync Meta karo', true);
      return;
    }
    setTemplateForm(templateFormFromMeta(tpl, {
      name: tpl.name,
      editingId: String(tpl.id),
      editingStatus: status,
    }));
    setTemplateSubTab('create');
    setAiTemplateBrief('');
    flash(`Editing "${tpl.name}" — save ke baad Meta phir se review karega`);
  };

  const startCloneTemplate = (tpl) => {
    if (!tpl) return;
    const cloneName = nextCloneTemplateName(tpl.name);
    setTemplateForm(templateFormFromMeta(tpl, { name: cloneName }));
    setTemplateSubTab('create');
    setAiTemplateBrief('');
    const mediaHint = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(String(tpl.header_type || '').toUpperCase())
      ? ' · media header ke liye sample file phir se upload karo'
      : '';
    flash(
      `Clone ready as "${cloneName}" — review karke Submit (naya template Meta pe create hoga)${mediaHint}`,
    );
  };

  const createTemplate = async () => {
    setTemplateSubmitting(true);
    try {
      if (!templateForm.name || !templateForm.body_text) {
        flash('Template name and body required', true);
        return;
      }
      if (!isEditingTemplate && templateForm.account_scope === 'single' && !templateForm.account_id && accounts.length) {
        flash('Select account or choose All Accounts', true);
        return;
      }
      if (isEditingTemplate && !templateForm.account_id) {
        flash('Edit ke liye account select karo (jahan template hai)', true);
        return;
      }
      if (templateForm.header_type === 'TEXT' && !templateForm.header_text?.trim()) {
        flash('Header text required', true);
        return;
      }
      if (
        !isEditingTemplate
        && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateForm.header_type)
        && !templateForm.header_media_base64
      ) {
        flash('Upload a sample file for media header (Meta approval)', true);
        return;
      }

      const bodyVarCount = countPlaceholders(templateForm.body_text);
      const body_examples = templateForm.body_examples.slice(0, bodyVarCount);
      const lockCategory = isEditingTemplate
        && String(templateForm.editing_status || '').toUpperCase() === 'APPROVED';

      const payload = {
        mode: isEditingTemplate ? 'edit' : 'create',
        template_id: isEditingTemplate ? templateForm.editing_template_id : undefined,
        lock_category: lockCategory || undefined,
        name: templateForm.name,
        body_text: templateForm.body_text,
        category: templateForm.category,
        language: templateForm.language,
        footer_text: templateForm.footer_text,
        header_type: templateForm.header_type,
        header_text: templateForm.header_text,
        header_text_examples: templateForm.header_text_examples,
        body_examples,
        buttons: templateForm.buttons.map((b) => ({
          type: b.type,
          text: b.text,
          url: b.url,
          url_examples: b.url_examples,
          phone_number: b.phone_number,
        })),
        account_scope: isEditingTemplate ? 'single' : templateForm.account_scope,
        account_id: isEditingTemplate
          ? templateForm.account_id
          : (templateForm.account_scope === 'single' ? templateForm.account_id : undefined),
      };

      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateForm.header_type) && templateForm.header_media_base64) {
        payload.header_media_base64 = templateForm.header_media_base64;
        payload.header_media_name = templateForm.header_media_name;
        payload.header_media_mime = templateForm.header_media_mime;
      }

      const json = await WhatsAppAutomationService.createTemplate(payload);
      const anyOk = json.success || (json.results || []).some((r) => r.success);
      if (!anyOk) {
        const detail = json.error
          || json.results?.find((r) => r.error)?.error
          || 'Submit failed';
        throw new Error(detail);
      }
      setTemplateResults(json.results || []);
      const submittedName = json.template_name || templateForm.name;
      setHighlightTemplateName(submittedName);
      setTemplateSubTab('library');
      if (submittedName) {
        setSelectedTemplateKey(`${submittedName}::${templateForm.language || 'en'}`);
      }
      flash(
        isEditingTemplate
          ? 'Template updated — Meta re-review usually 24–48 hours (purani version tab tak kaam kar sakti hai)'
          : 'Template submitted — Meta review usually 24–48 hours',
      );
      setTemplateForm(emptyTemplate());
      setAiTemplateBrief('');
      const syncId = templateForm.account_id || syncAccountId;
      await syncTemplates({ silent: true, accountId: syncId || null });
    } catch (e) {
      flash(e.message, true);
    } finally {
      setTemplateSubmitting(false);
    }
  };

  const sendBulk = async () => {
    if (!bulkForm.name) {
      flash('Campaign name required', true);
      return;
    }
    if (!bulkForm.account_id) {
      flash('Select automation account', true);
      return;
    }

    const segmentQueue = bulkSource === 'customers' && !retargetFromId;
    const segmentTotal = Number(stats?.customer_segments?.[bulkCustomerSegment] ?? 0);

    let recipients = [];
    if (segmentQueue) {
      if (!segmentTotal) {
        flash('No customers in selected filter — pick another segment or refresh stats', true);
        return;
      }
    } else if (bulkSource === 'manual') {
      const phones = bulkForm.phones
        .split(/[\n,;]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      if (!phones.length) {
        flash('Enter at least one phone number', true);
        return;
      }
      recipients = phones.map((phone) => ({
        phone,
        contact_name: 'Customer',
        variables: recipientToVariables({ phone, customer_name: 'Customer' }),
      }));
    } else if (!bulkRecipients.length) {
      flash(bulkSource === 'csv' ? 'Upload CSV with valid rows' : 'Load customers first', true);
      return;
    } else {
      recipients = bulkRecipients.map((r) => ({
        phone: r.phone,
        contact_name: r.customer_name || r.variables?.customer_name || 'Customer',
        variables: r.variables || recipientToVariables(r),
      }));
    }

    setBulkSending(true);
    try {
      let templateParams = [];
      if (bulkForm.message_mode === 'template') {
        try { templateParams = JSON.parse(bulkForm.template_params || '[]'); } catch { templateParams = []; }
        if (!bulkForm.template_name) {
          flash('Template name required', true);
          setBulkSending(false);
          return;
        }
        if (!Array.isArray(templateParams)) {
          flash('Template params must be a JSON array like ["{{customer_name}}","{{car}}"]', true);
          setBulkSending(false);
          return;
        }
        if (templateParams.some((p) => String(p ?? '').trim() === '')) {
          flash('Template params mein empty value mat chhodo — har slot ke liye variable select karo', true);
          setBulkSending(false);
          return;
        }
        const tpl = approvedBulkTemplates.find(
          (t) => t.name === bulkForm.template_name && (t.language || 'en') === (bulkForm.template_language || 'en'),
        ) || approvedBulkTemplates.find((t) => t.name === bulkForm.template_name);
        const needed = Number(tpl?.param_count || 0);
        if (needed > 0 && templateParams.length !== needed) {
          flash(`Is template ko ${needed} param(s) chahiye, aapne ${templateParams.length} diye`, true);
          setBulkSending(false);
          return;
        }
        const headerType = String(bulkForm.template_header_type || '').toUpperCase();
        if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType)) {
          const headerUrl = String(bulkForm.template_header_image_url || '').trim();
          const ephemeral = /scontent\.|fbcdn\.net|whatsapp\.net|fbsbx\.com|lookaside/i.test(headerUrl);
          if (!headerUrl || ephemeral) {
            flash(
              `Is template ka ${headerType} header hai — permanent public URL chahiye (Meta CDN #131053). Storage path: roadserve-uploads/whatsapp-templates/${bulkForm.template_name}_header.jpg`,
              true,
            );
            setBulkSending(false);
            return;
          }
        }
      } else if (!bulkForm.text_body?.trim()) {
        flash('Message text required', true);
        setBulkSending(false);
        return;
      }

      const json = await WhatsAppAutomationService.sendBulk({
        name: bulkForm.name,
        account_id: bulkForm.account_id,
        recipients: segmentQueue ? undefined : recipients,
        segment_queue: segmentQueue,
        expected_total: segmentQueue ? segmentTotal : recipients.length,
        message_mode: bulkForm.message_mode,
        text_body: bulkForm.text_body,
        template_name: bulkForm.template_name,
        template_language: bulkForm.template_language,
        template_params: templateParams,
        template_header_type: bulkForm.template_header_type || 'NONE',
        template_header_image_url: bulkForm.template_header_image_url || '',
        segment: bulkCustomerSegment,
        search: bulkCustomerSearch,
        state_filter: bulkCustomerState,
        track_links: bulkTrackLinks,
        parent_campaign_id: retargetFromId || undefined,
      });
      if (!json.success) throw new Error(json.error);

      if (json.async) {
        flash(`Campaign queued — ${json.total} recipients sending in background`);
        setBulkSubTab('campaigns');
        if (json.campaign_id) {
          openCampaignDetail(json.campaign_id);
        }
      } else if (json.failed > 0) {
        let errHint = '';
        try {
          const detail = await WhatsAppAutomationService.getBulkCampaignDetail(json.campaign_id);
          const failedRow = (detail?.recipients || []).find((r) => r.status === 'failed' && r.error);
          if (failedRow?.error) errHint = failedRow.error;
        } catch {
          // ignore
        }
        flash(
          `Sent ${json.sent}/${json.total} — ${json.failed} failed${errHint ? `: ${errHint}` : ''}`,
          true,
        );
      } else {
        flash(`Bulk sent: ${json.sent}/${json.total} success`);
      }
      setBulkForm(emptyBulk());
      setBulkRecipients([]);
      setBulkCsvErrors([]);
      setRetargetFromId(null);
      loadStats();
      loadBulkCampaigns();
    } catch (e) {
      flash(e.message, true);
    }
    setBulkSending(false);
  };

  const handleBulkCsvFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const { rows, errors } = parseBulkMessageCsv(text);
      setBulkRecipients(rows);
      setBulkCsvErrors(errors);
      flash(`CSV loaded: ${rows.length} recipients${errors.length ? `, ${errors.length} skipped` : ''}`);
    } catch (e) {
      setBulkRecipients([]);
      setBulkCsvErrors([]);
      flash(e.message || 'Invalid CSV', true);
    }
  };

  const saveAi = async () => {
    try {
      await WhatsAppAutomationService.saveAiMemory(aiMemory);
      flash('AI memory saved');
      loadStats();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const testAi = async () => {
    try {
      const json = await WhatsAppAutomationService.testAiReply(aiTestMsg);
      if (!json.success) throw new Error(json.error || json.reason);
      setAiTestReply(json.reply || '');
    } catch (e) {
      flash(e.message, true);
    }
  };

  const bySource = stats?.by_source && typeof stats.by_source === 'object' ? stats.by_source : {};
  const byStatus = stats?.by_status && typeof stats.by_status === 'object' ? stats.by_status : {};
  const byAccount = Array.isArray(stats?.by_account) ? stats.by_account : [];
  const bulkSendCountLabel = () => {
    if (bulkSource === 'manual') return 'numbers';
    if (bulkSource === 'customers' && !retargetFromId) {
      const n = Number(stats?.customer_segments?.[bulkCustomerSegment] ?? 0);
      return `${n.toLocaleString('en-IN')} customers`;
    }
    return `${(bulkRecipients.length || 0).toLocaleString('en-IN')} customers`;
  };

  const customerSegments = stats?.customer_segments && typeof stats.customer_segments === 'object'
    ? stats.customer_segments
    : EMPTY_STATS.customer_segments;

  return (
    <div className={`mx-auto ${section === 'chats' ? 'max-w-none' : 'max-w-7xl'}`}>
      <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur border-b border-gray-200 mb-3 -mx-1 px-1">
        <div className="flex items-center justify-between gap-2 py-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
              <Zap className="text-white" size={16} />
            </div>
            <h1 className="text-base font-bold text-gray-900 truncate">WA Automation</h1>
          </div>
          <button
            type="button"
            onClick={loadAll}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 shrink-0 px-2 py-1 rounded-md hover:bg-gray-100"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
        <div className="flex gap-0.5 overflow-x-auto pb-0 scrollbar-thin -mb-px">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = section === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                title={s.label}
                className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition shrink-0 ${
                  active
                    ? 'border-emerald-600 text-emerald-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/80'
                }`}
              >
                <Icon size={14} />
                <span className="hidden md:inline">{s.label}</span>
                <span className="md:hidden">{s.short}</span>
              </button>
            );
          })}
        </div>
      </div>

      {setupRequired && (
        <div className="mb-3 bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-lg text-xs">
          Database setup pending — Supabase SQL Editor mein run karo: <code>whatsapp-accounts-automation.sql</code>, <code>whatsapp-bulk-campaigns.sql</code>, <code>whatsapp-bulk-customers.sql</code>, <code>whatsapp-dashboard-alltime-stats.sql</code>, <code>whatsapp-bulk-campaign-insights.sql</code>
        </div>
      )}

      {error && (
        <div className="mb-3 flex items-center gap-2 bg-red-50 text-red-700 px-3 py-2 rounded-lg text-xs">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div className="mb-3 flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg text-xs">
          <CheckCircle2 size={14} /> {success}
        </div>
      )}

      {/* DASHBOARD */}
      {section === 'dashboard' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Users size={16} /> Customer Segments <span className="text-xs font-normal text-gray-400">(all time)</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {CUSTOMER_SEGMENTS.map((seg) => (
                <StatCard
                  key={seg.value}
                  label={seg.label}
                  value={customerSegments[seg.value] ?? 0}
                  tone={segmentCardTone(seg.value)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <LayoutDashboard size={16} /> Automation Overview <span className="text-xs font-normal text-gray-400">(all time)</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <StatCard label="Automation Accounts" value={stats?.accounts_total} sub={`${stats?.accounts_default || 0} default`} />
              <StatCard label="Active Rules" value={stats?.rules_enabled} sub={`${stats?.rules_total || 0} total`} tone="blue" />
              <StatCard label="Messages Today" value={stats?.messages_today} sub={`${stats?.messages_total || 0} all time`} />
              <StatCard label="Outbound" value={stats?.outbound_total} sub={`${stats?.inbound_total || 0} inbound`} />
              <StatCard label="Automation Sent" value={stats?.automation_sent} sub={`${stats?.automation_failed || 0} failed`} tone="amber" />
              <StatCard label="Bulk Sent" value={stats?.bulk_sent_total} sub={`${stats?.bulk_campaigns_total || 0} campaigns`} tone="purple" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><BarChart3 size={16} /> By Source</h3>
              {Object.keys(bySource).length === 0 ? <p className="text-sm text-gray-400">No data</p> : (
                <div className="space-y-2">
                  {Object.entries(bySource).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="capitalize text-gray-600">{k}</span>
                      <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><MessageSquare size={16} /> Delivery Status</h3>
              {Object.keys(byStatus).length === 0 ? <p className="text-sm text-gray-400">No data</p> : (
                <div className="space-y-2">
                  {Object.entries(byStatus).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="capitalize text-gray-600">{k}</span>
                      <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-800 mb-3">AI Status</h3>
              <p className={`text-lg font-bold ${stats?.ai_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
                {stats?.ai_enabled ? 'AI Enabled' : 'AI Disabled'}
              </p>
              <p className="text-xs text-gray-400 mt-2">Automation logs: {stats?.automation_log_total || 0}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Per Automation Account</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2">Account</th>
                    <th className="pb-2">Messages</th>
                    <th className="pb-2">Delivered</th>
                    <th className="pb-2">Read</th>
                    <th className="pb-2">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {(byAccount).map((row) => (
                    <tr key={row.id} className="border-b border-gray-50">
                      <td className="py-2 font-medium">{row.name}<br /><span className="text-xs text-gray-400">{row.display_phone}</span></td>
                      <td className="py-2">{row.message_count}</td>
                      <td className="py-2 text-emerald-600">{row.delivered}</td>
                      <td className="py-2 text-blue-600">{row.read_count}</td>
                      <td className="py-2 text-red-500">{row.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Recent Automation Logs</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(stats?.recent_automation_logs || []).map((log) => (
                  <div key={log.id} className="text-xs border rounded p-2">
                    <span className={`font-medium ${log.status === 'sent' ? 'text-emerald-600' : 'text-red-500'}`}>{log.status}</span>
                    {' '}{log.contact_phone} — {log.rule_name}
                    <p className="text-gray-400 truncate">{log.message_preview}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Recent Bulk Campaigns</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(stats?.recent_bulk_campaigns || []).map((c) => (
                  <div key={c.id} className="text-xs border rounded p-2">
                    <p className="font-medium">{c.name} <span className="text-gray-400">({c.account_name})</span></p>
                    <p>{c.sent_count}/{c.total_recipients} sent • {c.failed_count} failed • <span className="capitalize">{c.status}</span></p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CHATS */}
      {section === 'chats' && (
        accounts.length === 0 ? (
          <div className="bg-white rounded-xl border p-8 text-center text-gray-500 text-sm">
            Pehle <button type="button" onClick={() => setSection('accounts')} className="text-emerald-600 underline">Automation Keys</button> me account add karo — phir usi number ke chats yahan dikhenge.
          </div>
        ) : (
          <WhatsApp
            embedded
            automationAccountIds={accounts.filter((a) => a.is_active !== false).map((a) => a.id)}
            automationAccounts={accounts}
            defaultAccountId={accounts.find((a) => a.is_default)?.id || accounts[0]?.id}
          />
        )
      )}

      {/* ACCOUNTS */}
      {section === 'accounts' && (
        <div className="space-y-6">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-slate-50/80 px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                  <Key size={18} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">
                    {editingAccountId ? 'Edit Automation Account' : 'Add Automation Account'}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Meta Phone Number ID, WABA ID aur permanent token yahan add karo
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editingAccountId && accounts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAddAccountForm((open) => !open)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    {accountFormOpen ? <X size={14} /> : <Plus size={14} />}
                    {accountFormOpen ? 'Hide form' : 'Add account'}
                  </button>
                )}
                {editingAccountId && (
                  <button
                    type="button"
                    onClick={cancelAccountForm}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                  >
                    Cancel edit
                  </button>
                )}
              </div>
            </div>

            {accountFormOpen && (
              <div className="p-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <div>
                    <label className={accountLabelClass}>Account Name</label>
                    <input
                      type="text"
                      value={accountForm.name}
                      onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                      placeholder="RoadServe Automation 1"
                      className={accountInputClass}
                    />
                  </div>
                  <div>
                    <label className={accountLabelClass}>Display Phone</label>
                    <input
                      type="text"
                      value={accountForm.display_phone}
                      onChange={(e) => setAccountForm({ ...accountForm, display_phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className={accountInputClass}
                    />
                  </div>
                  <div>
                    <label className={accountLabelClass}>Verify Token</label>
                    <input
                      type="text"
                      value={accountForm.verify_token}
                      onChange={(e) => setAccountForm({ ...accountForm, verify_token: e.target.value })}
                      placeholder="roadserve_whatsapp_verify_token"
                      className={accountInputMonoClass}
                    />
                  </div>
                  <div>
                    <label className={accountLabelClass}>Phone Number ID *</label>
                    <input
                      type="text"
                      value={accountForm.phone_number_id}
                      onChange={(e) => setAccountForm({ ...accountForm, phone_number_id: e.target.value })}
                      placeholder="1087299044461102"
                      className={accountInputMonoClass}
                    />
                  </div>
                  <div>
                    <label className={accountLabelClass}>WABA ID *</label>
                    <input
                      type="text"
                      value={accountForm.waba_id}
                      onChange={(e) => setAccountForm({ ...accountForm, waba_id: e.target.value })}
                      placeholder="1166442448799535"
                      className={accountInputMonoClass}
                    />
                  </div>
                  <div className="md:col-span-2 xl:col-span-1">
                    <label className={accountLabelClass}>
                      {editingAccountId ? 'Permanent Token (blank = keep)' : 'Permanent Token *'}
                    </label>
                    <div className="relative">
                      <input
                        type={showPermanentToken ? 'text' : 'password'}
                        value={accountForm.permanent_token}
                        onChange={(e) => setAccountForm({ ...accountForm, permanent_token: e.target.value })}
                        placeholder="EAA..."
                        className={`${accountInputMonoClass} pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPermanentToken((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        title={showPermanentToken ? 'Hide token' : 'Show token'}
                      >
                        {showPermanentToken ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-slate-50/70 px-4 py-3">
                  <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={accountForm.is_default}
                        onChange={(e) => setAccountForm({ ...accountForm, is_default: e.target.checked })}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      Default account
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={accountForm.ai_enabled}
                        onChange={(e) => setAccountForm({ ...accountForm, ai_enabled: e.target.checked })}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      AI enabled
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={copyWebhook}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                    >
                      <Copy size={13} />
                      Webhook URL
                    </button>
                    {!editingAccountId && (
                      <button
                        type="button"
                        onClick={importFromSupabase}
                        disabled={importingEnv}
                        className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        {importingEnv ? 'Loading…' : 'Load from Supabase'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={saveAccount}
                      className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      <Save size={15} />
                      {editingAccountId ? 'Update Account' : 'Save Account'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 min-w-0 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-gray-800">Automation Accounts ({accounts.length})</h2>
                <p className="text-xs text-gray-500 mt-0.5">Account save hone ke baad Meta details yahan dikhengi</p>
              </div>
              {accounts.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    fetchedAccountDetailsKeyRef.current = '';
                    refreshAllAccountDetails(accounts);
                  }}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <RefreshCw size={13} />
                  Refresh all
                </button>
              )}
            </div>
            {accounts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 px-4 py-10 text-center">
                <MessageSquare size={28} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">Pehla automation account add karo</p>
                <p className="text-xs text-gray-400 mt-1">Save ke baad number status, quality aur limit yahan dikhega</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {accounts.map((acc) => (
                  <AutomationAccountCard
                    key={acc.id}
                    account={acc}
                    details={accountDetailsMap[acc.id]}
                    loading={accountDetailsLoading[acc.id]}
                    error={accountDetailsError[acc.id]}
                    testing={testingAccountId === acc.id}
                    deleting={deletingAccountId === acc.id}
                    onEdit={editAccount}
                    onTest={testAccount}
                    onRefresh={refreshAccountDetails}
                    onDelete={requestDeleteAccount}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* BULK */}
      {section === 'bulk' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setBulkSubTab('send')} className={`px-4 py-2 rounded-lg text-sm font-medium ${bulkSubTab === 'send' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}>New Campaign</button>
            <button type="button" onClick={() => { setBulkSubTab('campaigns'); loadBulkCampaigns({ syncMeta: true }); }} className={`px-4 py-2 rounded-lg text-sm font-medium ${bulkSubTab === 'campaigns' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}>Campaign Insights</button>
          </div>

          {bulkSubTab === 'campaigns' && (
            <div className="bg-white rounded-xl border p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">Campaign History & Insights</h2>
                <button
                  type="button"
                  onClick={() => loadBulkCampaigns({ syncMeta: true })}
                  className="text-sm text-emerald-700 flex items-center gap-1"
                >
                  <RefreshCw size={14} className={bulkCampaignsLoading ? 'animate-spin' : ''} /> Refresh
                </button>
              </div>
              {bulkCampaignsLoading && bulkCampaigns.length === 0 ? (
                <p className="text-sm text-gray-500">Loading campaigns…</p>
              ) : bulkCampaigns.length === 0 ? (
                <p className="text-sm text-gray-400">Abhi koi campaign nahi. Pehli bulk campaign bhejo.</p>
              ) : (
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-3">Campaign</th>
                        <th className="py-2 pr-3">Sent</th>
                        <th className="py-2 pr-3">Delivered %</th>
                        <th className="py-2 pr-3">Failed %</th>
                        <th className="py-2 pr-3">Read %</th>
                        <th className="py-2 pr-3">Replied %</th>
                        <th className="py-2 pr-3" title="Meta URL button + app.roadserve.in/go short-link clicks during campaign window">URL clicks %</th>
                        <th className="py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkCampaigns.map((c) => (
                        <tr key={c.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 pr-3">
                            <p className="font-medium">{c.name}</p>
                            <p className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString('en-IN')}</p>
                            <p className={`text-[10px] mt-0.5 capitalize ${
                              c.status === 'cancelled' ? 'text-amber-700'
                                : c.status === 'running' || c.status === 'pending' ? 'text-emerald-700'
                                  : c.status === 'failed' ? 'text-red-600' : 'text-gray-400'
                            }`}
                            >
                              {c.status}
                            </p>
                          </td>
                          <td className="py-2 pr-3">{c.sent_count}/{c.total_recipients}</td>
                          <td className="py-2 pr-3 text-emerald-700">{fmtPctWithCount(c.delivered_pct, c.delivered_count)}</td>
                          <td className="py-2 pr-3 text-red-600">{fmtPctWithCount(c.failed_pct, c.failed_count)}</td>
                          <td className="py-2 pr-3">{fmtPctWithCount(c.read_pct, c.read_count)}</td>
                          <td className="py-2 pr-3">{fmtPctWithCount(c.responded_pct, c.responded_count)}</td>
                          <td className="py-2 pr-3">
                            {fmtPctWithCount(
                              c.link_click_pct ?? c.meta_url_click_pct,
                              c.link_click_count ?? c.short_link_click_count ?? c.meta_url_click_unique ?? c.meta_url_click_count,
                            )}
                          </td>
                          <td className="py-2">
                            <div className="flex flex-col items-start gap-1">
                              <button type="button" onClick={() => openCampaignDetail(c.id)} className="text-xs text-emerald-700 hover:underline">View</button>
                              {['running', 'pending'].includes(c.status) && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!window.confirm(`Stop campaign "${c.name}"? Pending messages nahi jayenge.`)) return;
                                    try {
                                      const res = await WhatsAppAutomationService.cancelBulkCampaign(c.id);
                                      flash(`Campaign stopped — ${res?.skipped_pending ?? 0} pending skipped`);
                                      loadBulkCampaigns({ syncMeta: false });
                                      if (selectedCampaign === c.id) openCampaignDetail(c.id);
                                    } catch (e) {
                                      flash(e.message || 'Stop failed', true);
                                    }
                                  }}
                                  className="text-xs text-red-600 hover:underline"
                                >
                                  Stop
                                </button>
                              )}
                              {['cancelled', 'failed'].includes(c.status) && (
                                <button
                                  type="button"
                                  onClick={() => handleResumeBulkCampaign(c.id, c.name)}
                                  className="text-xs text-emerald-700 hover:underline"
                                >
                                  Resume
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {campaignDetail?.stats && selectedCampaign && (
                <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-semibold text-sm">
                      {campaignDetail.campaign?.name} — Detail
                      <span className="ml-2 text-xs font-normal capitalize text-gray-500">({campaignDetail.campaign?.status})</span>
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {['running', 'pending'].includes(campaignDetail.campaign?.status) && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm('Is campaign ko abhi stop karna hai? Jo pending hain woh nahi jayenge. Jo already sent ho chuke unko undo nahi kiya ja sakta.')) return;
                            try {
                              const res = await WhatsAppAutomationService.cancelBulkCampaign(selectedCampaign);
                              flash(`Campaign stopped — ${res?.skipped_pending ?? 0} pending skipped`);
                              loadBulkCampaigns({ syncMeta: false });
                              openCampaignDetail(selectedCampaign);
                            } catch (e) {
                              flash(e.message || 'Stop failed', true);
                            }
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-500"
                        >
                          Stop campaign
                        </button>
                      )}
                      {['running', 'pending', 'failed', 'cancelled'].includes(campaignDetail.campaign?.status) && (
                        <button
                          type="button"
                          onClick={() => handleResumeBulkCampaign(
                            selectedCampaign,
                            campaignDetail.campaign?.name,
                          )}
                          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500"
                        >
                          Resume sending
                        </button>
                      )}
                    </div>
                  </div>
                  {['running', 'pending'].includes(campaignDetail.campaign?.status) && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                      Beech mein rokna ho to <strong>Stop campaign</strong> dabao. Resume sirf jab stuck ho — dobara mat dabao warna rate-limit aata hai.
                    </p>
                  )}
                  {campaignDetail.campaign?.status === 'cancelled' && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                      Campaign stopped. Jo already Meta ko chale gaye unhe undo nahi kar sakte.
                      {' '}<strong>Resume sending</strong> dabao — baaki pending (skipped) messages dubara jayenge.
                    </p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="bg-white rounded p-2 border">Delivered: <strong>{campaignDetail.stats.delivered_pct}%</strong> ({campaignDetail.stats.delivered_count})</div>
                    <div className="bg-white rounded p-2 border">Send failed: <strong>{campaignDetail.stats.failed_pct}%</strong> ({campaignDetail.stats.failed_count})</div>
                    <div className="bg-white rounded p-2 border" title="Meta accepted send but phone couldn't receive (#131026)">
                      Undelivered: <strong>{campaignDetail.stats.undelivered_pct ?? 0}%</strong> ({campaignDetail.stats.undelivered_count ?? 0})
                    </div>
                    <div className="bg-white rounded p-2 border">Read: <strong>{campaignDetail.stats.read_pct}%</strong> ({campaignDetail.stats.read_count})</div>
                    <div className="bg-white rounded p-2 border">Replied: <strong>{campaignDetail.stats.responded_pct}%</strong> ({campaignDetail.stats.responded_count})</div>
                    <div className="bg-white rounded p-2 border" title="Meta URL button + app.roadserve.in/go short-link during campaign window">
                      URL clicks: <strong>{campaignDetail.stats.link_click_pct ?? campaignDetail.stats.meta_url_click_pct ?? 0}%</strong>
                      {' '}({campaignDetail.stats.link_click_count ?? campaignDetail.stats.short_link_click_count ?? campaignDetail.stats.meta_url_click_unique ?? 0})
                      {Number(campaignDetail.stats.meta_url_click_unique || 0) > 0 && (
                        <span className="text-gray-400"> · Meta btn: {campaignDetail.stats.meta_url_click_unique}</span>
                      )}
                    </div>
                    <div className="bg-white rounded p-2 border">No response: <strong>{campaignDetail.stats.no_response_count}</strong></div>
                  </div>
                  {Array.isArray(campaignDetail.button_stats) && campaignDetail.button_stats.length > 0 && (
                    <div className="bg-white rounded-lg border p-3 space-y-2">
                      <h4 className="text-xs font-semibold text-gray-700">Template button clicks</h4>
                      <div className="overflow-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="py-1.5 pr-3">Button</th>
                              <th className="py-1.5 pr-3">Clicks</th>
                              <th className="py-1.5">% of sent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaignDetail.button_stats.map((row) => (
                              <tr key={row.label} className="border-b last:border-0">
                                <td className="py-1.5 pr-3 font-medium text-gray-800">{row.label}</td>
                                <td className="py-1.5 pr-3">{row.count}</td>
                                <td className="py-1.5">{row.pct ?? 0}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-[11px] text-gray-400">Quick reply buttons only. URL/phone buttons Meta se click count nahi bhejta.</p>
                    </div>
                  )}
                  {(() => {
                    const { stats: failureStats, failedRecipients } = buildFailureInsights(campaignDetail);
                    if (failureStats.length === 0) return null;
                    return (
                      <div className="bg-white rounded-lg border p-3 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h4 className="text-xs font-semibold text-red-700">Failed — reasons</h4>
                          <button
                            type="button"
                            onClick={handleRetryAllRetryableFailures}
                            className="text-[11px] px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                          >
                            Retry all safe failures
                          </button>
                        </div>
                        <div className="overflow-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-500 border-b">
                                <th className="py-1.5 pr-3">Reason</th>
                                <th className="py-1.5 pr-3">Count</th>
                                <th className="py-1.5 pr-3">%</th>
                                <th className="py-1.5">Retry</th>
                              </tr>
                            </thead>
                            <tbody>
                              {failureStats.map((row) => (
                                <tr key={row.reason} className="border-b last:border-0">
                                  <td className="py-1.5 pr-3 text-red-800" title={row.retry_hint || ''}>
                                    {row.reason}
                                    {row.retryable && (
                                      <span className="ml-1 text-[10px] text-emerald-700">(safe)</span>
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-3">{row.count}</td>
                                  <td className="py-1.5 pr-3">{row.pct ?? 0}%</td>
                                  <td className="py-1.5">
                                    <button
                                      type="button"
                                      onClick={() => handleRetryFailedReason(row)}
                                      className={`text-[11px] hover:underline ${
                                        row.retryable ? 'text-emerald-700' : 'text-amber-700'
                                      }`}
                                      title={row.retry_hint || ''}
                                    >
                                      {row.retryable ? 'Retry' : 'Retry anyway'}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {failedRecipients.length > 0 && (
                          <div className="overflow-auto border-t pt-2">
                            <p className="text-[11px] font-medium text-gray-600 mb-1">Failed recipients</p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-1 pr-3">Customer</th>
                                  <th className="py-1 pr-3">Phone</th>
                                  <th className="py-1">Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {failedRecipients.map((r) => (
                                  <tr key={r.id || `${r.phone}-${r.error}`} className="border-b last:border-0">
                                    <td className="py-1 pr-3">{r.contact_name || 'Customer'}</td>
                                    <td className="py-1 pr-3 font-mono">{fmtCampaignPhone(r.phone)}</td>
                                    <td className="py-1 text-red-700">{r.error || 'Unknown error'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500 w-full">Retarget as new campaign:</span>
                    {RETARGET_FILTERS.map((f) => (
                      <button key={f.value} type="button" onClick={() => loadRetarget(selectedCampaign, f.value)} className="text-xs px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-800 bg-white hover:bg-emerald-50">
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {bulkSubTab === 'send' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white rounded-xl border p-5 shadow-sm space-y-4">
            <div>
              <h2 className="font-semibold text-gray-800">Bulk WhatsApp Message</h2>
              <p className="text-xs text-gray-500 mt-1">
                Our Customers: filter select karke Queue send — full segment DB cache se jaati hai (1L+ timeout-free). Preview optional. Campaign insights: delivery/read/reply/click.
              </p>
            </div>

            <input value={bulkForm.name} onChange={(e) => setBulkForm({ ...bulkForm, name: e.target.value })} placeholder="Campaign name *" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <select
              value={bulkForm.account_id}
              onChange={(e) => {
                const accountId = e.target.value;
                setBulkForm((f) => ({ ...f, account_id: accountId, template_name: '', template_language: 'en' }));
                syncTemplates({ silent: true, accountId: accountId || null });
              }}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select automation account *</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>

            <div className="flex flex-wrap gap-2">
              {BULK_SOURCES.map((src) => (
                <button
                  key={src.value}
                  type="button"
                  onClick={() => { setBulkSource(src.value); setBulkRecipients([]); setBulkCsvErrors([]); }}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${bulkSource === src.value ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}
                >
                  {src.value === 'customers' && <Users size={14} className="inline mr-1 -mt-0.5" />}
                  {src.value === 'csv' && <Upload size={14} className="inline mr-1 -mt-0.5" />}
                  {src.label}
                </button>
              ))}
            </div>

            {bulkSource === 'customers' && (
              <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <select value={bulkCustomerSegment} onChange={(e) => setBulkCustomerSegment(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white">
                    {CUSTOMER_SEGMENTS.map((s) => (
                      <option key={s.value} value={s.value}>{fmtSegmentOption(s, customerSegments)}</option>
                    ))}
                  </select>
                  <select value={bulkCustomerState} onChange={(e) => setBulkCustomerState(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">All states</option>
                    {INDIAN_STATES.filter(Boolean).map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                  <input
                    value={bulkCustomerSearch}
                    onChange={(e) => setBulkCustomerSearch(e.target.value)}
                    placeholder="Search name / phone / car"
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Send queues the full segment from DB cache (1L+ OK). Preview shows max 100 rows — browser mein full list load nahi hota.
                  {bulkAudienceCache?.refreshed_at
                    ? ` Cache: ${Number(bulkAudienceCache.row_count || 0).toLocaleString('en-IN')} phones · ${new Date(bulkAudienceCache.refreshed_at).toLocaleString('en-IN')}`
                    : ' Cache not built yet.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => refreshBulkAudienceCache()}
                    disabled={bulkAudienceRefreshing}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={bulkAudienceRefreshing ? 'animate-spin' : ''} />
                    {bulkAudienceRefreshing ? 'Building audience cache…' : 'Refresh audience cache'}
                  </button>
                  <button
                    type="button"
                    onClick={() => loadBulkCustomers()}
                    disabled={bulkCustomersLoading}
                    className="px-4 py-2 bg-white border border-emerald-200 text-emerald-700 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    <Users size={14} />
                    {bulkCustomersLoading
                      ? 'Loading preview…'
                      : `Preview (${Math.min(100, bulkPreviewTotal || 100)})`}
                  </button>
                </div>
              </div>
            )}

            {bulkSource === 'csv' && (
              <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm cursor-pointer flex items-center gap-2 hover:bg-gray-50">
                    <Upload size={14} /> Upload CSV
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={(e) => handleBulkCsvFile(e.target.files?.[0])}
                    />
                  </label>
                  <button type="button" onClick={downloadBulkCsvSample} className="text-sm text-emerald-700 flex items-center gap-1 hover:underline">
                    <Download size={14} /> Sample CSV
                  </button>
                </div>
                <p className="text-xs text-gray-500">Required: <code>phone</code>. Optional: customer_name, membership_number, plan_name, start_date, expiry_date, car, car_model, state, pincode, membership_status, email</p>
                {bulkCsvErrors.length > 0 && (
                  <p className="text-xs text-amber-700">{bulkCsvErrors.length} row(s) skipped (invalid phone)</p>
                )}
              </div>
            )}

            {bulkSource === 'manual' && (
              <textarea
                value={bulkForm.phones}
                onChange={(e) => setBulkForm({ ...bulkForm, phones: e.target.value })}
                placeholder="Phone numbers — ek line par ek (10 digit ya 91...)"
                rows={6}
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              />
            )}

            {(bulkSource === 'customers' || bulkSource === 'csv') && bulkRecipients.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-600 border-b flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    {bulkSource === 'customers'
                      ? `Preview ${bulkRecipients.length.toLocaleString('en-IN')} of ${Number(bulkPreviewTotal || stats?.customer_segments?.[bulkCustomerSegment] || 0).toLocaleString('en-IN')} — send queues full segment`
                      : `${bulkRecipients.length.toLocaleString('en-IN')} recipients loaded for send`}
                  </span>
                  {bulkSource === 'customers' && customerSegments[bulkCustomerSegment] != null && (
                    <span className="text-gray-400">· filter total: {Number(customerSegments[bulkCustomerSegment]).toLocaleString('en-IN')}</span>
                  )}
                  <span className="text-gray-400">· preview below (scroll for more)</span>
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-white sticky top-0">
                      <tr className="text-left text-gray-500">
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Phone</th>
                        <th className="px-3 py-2">Membership</th>
                        <th className="px-3 py-2">Expiry</th>
                        <th className="px-3 py-2">App</th>
                        <th className="px-3 py-2">Car</th>
                        <th className="px-3 py-2">Model</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRecipients.map((r, idx) => (
                        <tr key={`${r.phone}-${idx}`} className="border-t">
                          <td className="px-3 py-2">{r.customer_name || r.variables?.customer_name}</td>
                          <td className="px-3 py-2 font-mono">{r.phone}</td>
                          <td className="px-3 py-2 capitalize">{r.membership_status || r.variables?.membership_status || '—'}</td>
                          <td className="px-3 py-2">{r.expiry_date || r.variables?.expiry_date || '—'}</td>
                          <td className="px-3 py-2">
                            {r.app_installed || r.variables?.app_installed === 'Yes' ? (
                              <span className="text-emerald-700 font-medium">Installed</span>
                            ) : (
                              <span className="text-gray-400">No app</span>
                            )}
                          </td>
                          <td className="px-3 py-2">{r.car || r.variables?.car || '—'}</td>
                          <td className="px-3 py-2">{r.car_model || r.variables?.car_model || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <select value={bulkForm.message_mode} onChange={(e) => setBulkForm({ ...bulkForm, message_mode: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="text">Free Text (with variables)</option>
              <option value="template">Template (with variables)</option>
            </select>
            {bulkForm.message_mode === 'template' ? (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Approved template *</label>
                  <select
                    value={bulkForm.template_name ? `${bulkForm.template_name}::${bulkForm.template_language || 'en'}` : ''}
                    onChange={(e) => onBulkTemplatePick(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Select approved template…</option>
                    {approvedBulkTemplates.map((t) => (
                      <option key={`${t.name}::${t.language}`} value={`${t.name}::${t.language}`}>
                        {t.name} · {t.language}{t.param_count > 0 ? ` · ${t.param_count} variable(s)` : ''}
                      </option>
                    ))}
                  </select>
                  {approvedBulkTemplates.length === 0 && (
                    <p className="text-[11px] text-amber-700 mt-1">
                      No approved templates — Meta approve hone ke baad yahan dikhenge.{' '}
                      <button type="button" onClick={() => syncTemplates()} className="underline font-medium">Refresh</button>
                    </p>
                  )}
                  {bulkForm.template_name && (
                    <p className="text-[11px] text-gray-500 mt-1 font-mono">
                      {bulkForm.template_name} · {bulkForm.template_language || 'en'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Template params (JSON array)</label>
                  <input value={bulkForm.template_params} onChange={(e) => setBulkForm({ ...bulkForm, template_params: e.target.value })} placeholder='["{{customer_name}}","{{car}}"]' className="w-full border rounded-lg px-3 py-2 text-sm font-mono" />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Har {'{{'}1{'}}'} ke liye ek value — empty mat chhodo (Meta #132012). Variables neeche se add karo.
                  </p>
                  {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(String(bulkForm.template_header_type || '').toUpperCase()) && (
                    <div className="mt-2 space-y-1.5">
                      <label className="text-xs font-medium text-amber-800 block">
                        Permanent header {bulkForm.template_header_type} URL (required)
                      </label>
                      <input
                        value={bulkForm.template_header_image_url || ''}
                        onChange={(e) => setBulkForm({ ...bulkForm, template_header_image_url: e.target.value })}
                        placeholder="https://...supabase.co/storage/v1/object/public/roadserve-uploads/whatsapp-templates/TEMPLATE_header.jpg"
                        className="w-full border border-amber-200 rounded-lg px-3 py-2 text-xs font-mono bg-amber-50/40"
                      />
                      <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                        Meta CDN images expire → error #131053 (403). Supabase Storage mein public file upload karo:
                        {' '}
                        <code className="text-[10px] bg-white px-1 rounded">
                          roadserve-uploads/whatsapp-templates/{bulkForm.template_name || 'TEMPLATE'}_header.jpg
                        </code>
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <textarea
                value={bulkForm.text_body}
                onChange={(e) => setBulkForm({ ...bulkForm, text_body: e.target.value })}
                placeholder="Hi {{customer_name}}, your car {{car}} membership expires on {{expiry_date}}. Visit https://roadserve.in/renew"
                rows={4}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            )}
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={bulkTrackLinks} onChange={(e) => setBulkTrackLinks(e.target.checked)} />
              Track link clicks (message mein https:// link honi chahiye)
            </label>
            {retargetFromId && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">Retarget campaign — parent: {retargetFromId.slice(0, 8)}...</p>
            )}
            <button onClick={sendBulk} disabled={bulkSending} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
              <Send size={16} /> {bulkSending ? 'Submitting...' : `Queue send to ${bulkSendCountLabel()}`}
            </button>
          </div>

          <div className="bg-white rounded-xl border p-5 shadow-sm h-fit">
            <h3 className="font-semibold text-gray-800 mb-2">Available Variables</h3>
            <p className="text-xs text-gray-500 mb-3">Message ya template params mein use karo — har customer ke liye auto-fill hoga.</p>
            <div className="flex flex-wrap gap-2">
              {BULK_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => {
                    const token = `{{${v.key}}}`;
                    if (bulkForm.message_mode === 'template') {
                      let params = [];
                      try { params = JSON.parse(bulkForm.template_params || '[]'); } catch { params = []; }
                      if (!Array.isArray(params)) params = [];
                      setBulkForm({ ...bulkForm, template_params: JSON.stringify([...params, token]) });
                    } else {
                      setBulkForm({ ...bulkForm, text_body: `${bulkForm.text_body || ''}${bulkForm.text_body ? ' ' : ''}${token}` });
                    }
                  }}
                  className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-100 hover:bg-emerald-100"
                >
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
            <div className="mt-4 text-xs text-gray-500 space-y-1">
              <p><strong>All customers:</strong> No 2000 limit — sab matching load honge</p>
              <p><strong>Link tracking:</strong> Pehli https:// link track hogi (click count)</p>
              <p><strong>Insights:</strong> Campaign Insights tab — delivered %, failed %, read, reply, retarget</p>
            </div>
          </div>
        </div>
          )}
        </div>
      )}

      {/* RULES */}
      {section === 'rules' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border p-5 space-y-3">
            <h2 className="font-semibold">{editingRuleId ? 'Edit Rule' : 'New Rule'}</h2>
            <input value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} placeholder="Rule name" className="w-full border rounded-lg px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <select value={ruleForm.trigger_type} onChange={(e) => setRuleForm({ ...ruleForm, trigger_type: e.target.value, trigger_value: (TRIGGER_VALUES[e.target.value] || ['*'])[0] })} className="border rounded-lg px-3 py-2 text-sm">
                {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select value={ruleForm.trigger_value} onChange={(e) => setRuleForm({ ...ruleForm, trigger_value: e.target.value })} className="border rounded-lg px-3 py-2 text-sm">
                {(TRIGGER_VALUES[ruleForm.trigger_type] || ['*']).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">Send from automation account</p>
              {SENDER_MODES.map((m) => (
                <label key={m.value} className="flex items-center gap-2 text-sm mb-1">
                  <input type="radio" name="sender_mode" checked={ruleForm.sender_mode === m.value} onChange={() => setRuleForm({ ...ruleForm, sender_mode: m.value })} />
                  {m.label}
                </label>
              ))}
              {ruleForm.sender_mode === 'specific_account' && (
                <select value={ruleForm.account_id} onChange={(e) => setRuleForm({ ...ruleForm, account_id: e.target.value })} className="w-full mt-2 border rounded-lg px-3 py-2 text-sm">
                  <option value="">Select account...</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              )}
            </div>
            <select value={ruleForm.message_mode} onChange={(e) => setRuleForm({ ...ruleForm, message_mode: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="text">Text</option>
              <option value="template">Template</option>
            </select>
            {ruleForm.message_mode === 'template' ? (
              <>
                <input value={ruleForm.template_name} onChange={(e) => setRuleForm({ ...ruleForm, template_name: e.target.value })} placeholder="Template name" className="w-full border rounded-lg px-3 py-2 text-sm" />
                <input value={ruleForm.template_params} onChange={(e) => setRuleForm({ ...ruleForm, template_params: e.target.value })} placeholder='["{{customer_name}}"]' className="w-full border rounded-lg px-3 py-2 text-sm font-mono" />
              </>
            ) : (
              <textarea value={ruleForm.text_body} onChange={(e) => setRuleForm({ ...ruleForm, text_body: e.target.value })} placeholder="Message body" rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
            )}
            <button onClick={saveRule} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm">Save Rule</button>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold mb-3">Rules ({rules.length})</h3>
              {rules.map((rule) => (
                <div key={rule.id} className="border-b py-2 flex justify-between">
                  <div>
                    <p className="text-sm font-medium">{rule.name}</p>
                    <p className="text-xs text-gray-500">{rule.trigger_type} → {rule.trigger_value}</p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <button onClick={() => WhatsAppAutomationService.toggleRule(rule.id, !rule.is_enabled).then(loadRules)}>
                      {rule.is_enabled ? <ToggleRight className="text-emerald-600" size={20} /> : <ToggleLeft className="text-gray-400" size={20} />}
                    </button>
                    <button onClick={() => editRule(rule)} className="text-xs text-gray-500">Edit</button>
                    <button onClick={() => WhatsAppAutomationService.deleteRule(rule.id).then(loadRules)} className="text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TEMPLATES */}
      {section === 'templates' && (
        <div className="space-y-3">
          {/* Toolbar */}
          <div className="bg-white rounded-xl border px-3 py-2.5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                type="button"
                onClick={() => setTemplateSubTab('library')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${templateSubTab === 'library' ? 'bg-white shadow text-emerald-700' : 'text-gray-600 hover:text-gray-800'}`}
              >
                Template Library ({templateCounts.all})
              </button>
              <button
                type="button"
                onClick={() => {
                  setTemplateForm(emptyTemplate());
                  setTemplateSubTab('create');
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center gap-1 ${templateSubTab === 'create' ? 'bg-white shadow text-emerald-700' : 'text-gray-600 hover:text-gray-800'}`}
              >
                <Plus size={12} /> {isEditingTemplate ? 'Editing…' : 'Create New'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={syncAccountId}
                onChange={(e) => {
                  setSyncAccountId(e.target.value);
                  syncTemplates({ silent: true, accountId: e.target.value || null });
                }}
                className="border rounded-lg px-2 py-1.5 text-xs bg-white"
              >
                <option value="">Default account</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => syncTemplates()}
                disabled={templatesSyncing}
                className="text-xs flex items-center gap-1 px-3 py-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw size={12} className={templatesSyncing ? 'animate-spin' : ''} />
                {templatesSyncing ? 'Syncing…' : 'Sync Meta'}
              </button>
            </div>
          </div>

          {templateResults.length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs">
              {templateResults.map((r, i) => (
                <span key={i} className={`px-3 py-1.5 rounded-lg border ${r.success ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                  {r.account_name}: {r.success ? `Submitted (${r.template_name})` : r.error}
                </span>
              ))}
            </div>
          )}

          {templateSubTab === 'library' && (
            <>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: 'All', count: templateCounts.all },
                  { key: 'pending', label: 'In review', count: templateCounts.pending },
                  { key: 'approved', label: 'Approved', count: templateCounts.approved },
                  { key: 'rejected', label: 'Rejected', count: templateCounts.rejected },
                ].map((pill) => (
                  <button
                    key={pill.key}
                    type="button"
                    onClick={() => setTemplateStatusFilter(pill.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                      templateStatusFilter === pill.key
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {pill.label} <span className="opacity-80">({pill.count})</span>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,340px)_1fr] gap-4 items-start">
                {/* List sidebar */}
                <div className="bg-white rounded-xl border flex flex-col max-h-[calc(100vh-240px)] min-h-[420px]">
                  <div className="px-3 py-2.5 border-b bg-gray-50 rounded-t-xl">
                    <p className="text-xs font-semibold text-gray-700">Your templates</p>
                    <p className="text-[10px] text-gray-500">Live from Meta Business Manager</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {templatesSyncing && templates.length === 0 ? (
                      <div className="text-center py-12 text-sm text-gray-400">
                        <RefreshCw size={18} className="animate-spin mx-auto mb-2" />
                        Loading…
                      </div>
                    ) : sortedTemplates.length === 0 ? (
                      <div className="text-center py-12 px-4">
                        <p className="text-sm text-gray-500 mb-2">No templates found</p>
                        <button type="button" onClick={() => setTemplateSubTab('create')} className="text-xs text-emerald-600 font-medium hover:underline">
                          + Create your first template
                        </button>
                      </div>
                    ) : (
                      sortedTemplates.map((t) => (
                        <TemplateListItem
                          key={templateKey(t)}
                          template={t}
                          selected={selectedTemplate && templateKey(selectedTemplate) === templateKey(t)}
                          onClick={() => setSelectedTemplateKey(templateKey(t))}
                        />
                      ))
                    )}
                  </div>
                </div>

                {/* Detail panel */}
                <div className="min-h-[420px]">
                  {selectedTemplate ? (
                    <MetaTemplateCard
                      template={selectedTemplate}
                      highlighted={highlightTemplateName && selectedTemplate.name === highlightTemplateName}
                      onEdit={startEditTemplate}
                      onClone={startCloneTemplate}
                    />
                  ) : (
                    <div className="bg-white rounded-xl border h-full min-h-[420px] flex flex-col items-center justify-center text-center p-8">
                      <FileText size={40} className="text-gray-300 mb-3" />
                      <p className="text-sm text-gray-500">Select a template from the list</p>
                      <p className="text-xs text-gray-400 mt-1">Or create a new one</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {templateSubTab === 'create' && (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 items-start">
              <div className="bg-white rounded-xl border p-4 space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="font-semibold text-sm">
                      {isEditingTemplate ? 'Edit Meta Template' : 'Create Meta Template'}
                    </h2>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {isEditingTemplate
                        ? 'Name/language lock · Approved category lock · Save = Meta re-review'
                        : <>Meta review 24–48h · Media headers need <code className="text-[10px] bg-gray-100 px-1 rounded">WHATSAPP_APP_ID</code></>}
                    </p>
                  </div>
                  {isEditingTemplate && (
                    <button
                      type="button"
                      onClick={() => {
                        setTemplateForm(emptyTemplate());
                        setTemplateSubTab('library');
                      }}
                      className="text-xs px-2.5 py-1.5 rounded-lg border text-gray-600 hover:bg-gray-50"
                    >
                      Cancel edit
                    </button>
                  )}
                </div>

                {isEditingTemplate && (
                  <div className="text-xs bg-amber-50 border border-amber-100 text-amber-900 rounded-lg px-3 py-2">
                    Editing <span className="font-mono font-semibold">{templateForm.name}</span>
                    {' '}({templateForm.editing_status}). Body / header / footer / buttons change kar sakte ho.
                    Approved templates: max 1 edit / 24h, 10 / 30 days (Meta limit).
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Category</label>
                    <select
                      value={templateForm.category}
                      onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                      disabled={isEditingTemplate && templateForm.editing_status === 'APPROVED'}
                      className="w-full mt-0.5 border rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      {TEMPLATE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Language</label>
                    <select
                      value={templateForm.language}
                      onChange={(e) => setTemplateForm({ ...templateForm, language: e.target.value })}
                      disabled={isEditingTemplate}
                      className="w-full mt-0.5 border rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      {TEMPLATE_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">{isEditingTemplate ? 'Account (required)' : 'Submit to'}</p>
                  {!isEditingTemplate && (
                    <div className="flex flex-wrap gap-3 text-sm">
                      <label className="flex items-center gap-1.5">
                        <input type="radio" checked={templateForm.account_scope === 'single'} onChange={() => setTemplateForm({ ...templateForm, account_scope: 'single' })} />
                        Single account
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input type="radio" checked={templateForm.account_scope === 'all_accounts'} onChange={() => setTemplateForm({ ...templateForm, account_scope: 'all_accounts' })} />
                        All automation accounts
                      </label>
                    </div>
                  )}
                  {(isEditingTemplate || templateForm.account_scope === 'single') && (
                    <select value={templateForm.account_id} onChange={(e) => setTemplateForm({ ...templateForm, account_id: e.target.value })} className="w-full mt-1.5 border rounded-lg px-2 py-1.5 text-sm">
                      <option value="">Select account...</option>
                      {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600">Template name</label>
                  <input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    placeholder="order_update_v1"
                    disabled={isEditingTemplate}
                    className="w-full mt-0.5 border rounded-lg px-2 py-1.5 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>

                <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                  <label className="text-xs font-semibold text-gray-700">Header (optional)</label>
                  <select
                    value={templateForm.header_type}
                    onChange={(e) => setTemplateForm({
                      ...templateForm,
                      header_type: e.target.value,
                      header_media_base64: '',
                      header_media_name: '',
                    })}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white"
                  >
                    {HEADER_TYPES.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                  {templateForm.header_type === 'TEXT' && (
                    <>
                      <input
                        value={templateForm.header_text}
                        onChange={(e) => setTemplateForm({ ...templateForm, header_text: e.target.value })}
                        placeholder="Header text (use {{1}} for variable)"
                        className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white"
                      />
                      {countPlaceholders(templateForm.header_text) > 0 && (
                        <input
                          value={templateForm.header_text_examples[0] || ''}
                          onChange={(e) => setTemplateForm({ ...templateForm, header_text_examples: [e.target.value] })}
                          placeholder="Sample for {{1}}"
                          className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white"
                        />
                      )}
                    </>
                  )}
                  {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateForm.header_type) && (
                    <div>
                      <input
                        type="file"
                        accept={templateForm.header_type === 'IMAGE' ? 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png' : templateForm.header_type === 'VIDEO' ? 'video/mp4,.mp4' : 'application/pdf,.pdf'}
                        onChange={onHeaderMediaPick}
                        className="w-full text-xs"
                      />
                      <p className="text-[10px] text-gray-500 mt-1">
                        {templateForm.header_type === 'IMAGE' ? 'JPG or PNG only, max 5MB (no WEBP)' : templateForm.header_type === 'VIDEO' ? 'MP4 only, max 15MB' : 'PDF only, max 5MB'}
                      </p>
                      {templateForm.header_media_name && (
                        <p className="text-[11px] text-emerald-600 mt-1">Sample: {templateForm.header_media_name}</p>
                      )}
                      {isEditingTemplate && !templateForm.header_media_name && (
                        <p className="text-[11px] text-amber-700 mt-1">
                          Optional on edit — agar file nahi doge to Storage se
                          {' '}
                          <code className="text-[10px] bg-white px-1 rounded">
                            {templateForm.name}_header.jpg
                          </code>
                          {' '}reuse hoga (agar maujood ho).
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="border rounded-lg p-3 bg-violet-50 border-violet-200 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs font-semibold text-violet-900 flex items-center gap-1">
                      <Brain size={14} /> AI se template generate
                    </label>
                    <span className="text-[10px] text-violet-700 capitalize">{templateForm.category.toLowerCase()} rules</span>
                  </div>
                  <p className="text-[11px] text-violet-800">
                    {templateForm.category === 'UTILITY' && 'Utility = transactional only — marketing language Meta reject karega.'}
                    {templateForm.category === 'MARKETING' && 'Marketing = offers, renewal, promotion.'}
                    {templateForm.category === 'AUTHENTICATION' && 'Authentication = OTP / verify code only.'}
                  </p>
                  <textarea
                    value={aiTemplateBrief}
                    onChange={(e) => setAiTemplateBrief(e.target.value)}
                    placeholder={aiBriefPlaceholder()}
                    rows={2}
                    className="w-full border border-violet-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                  />
                  <button
                    type="button"
                    onClick={generateTemplateWithAi}
                    disabled={aiTemplateGenerating || !aiTemplateBrief.trim()}
                    className="w-full py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {aiTemplateGenerating ? <RefreshCw size={16} className="animate-spin" /> : <Brain size={16} />}
                    {aiTemplateGenerating ? 'Generating…' : 'Generate with AI'}
                  </button>
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-medium text-gray-600">Body *</label>
                    <button
                      type="button"
                      onClick={() => {
                        const n = countPlaceholders(templateForm.body_text) + 1;
                        setTemplateBody(`${templateForm.body_text}{{${n}}}`);
                      }}
                      className="text-[11px] text-emerald-600 hover:underline"
                    >
                      + Add {'{{'} var {'}}'}
                    </button>
                  </div>
                  <textarea
                    value={templateForm.body_text}
                    onChange={(e) => setTemplateBody(e.target.value)}
                    placeholder="Hello {{1}}, your order {{2}} is ready."
                    rows={5}
                    className="w-full mt-0.5 border rounded-lg px-2 py-1.5 text-sm"
                  />
                  {countPlaceholders(templateForm.body_text) > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-[11px] text-gray-500">Sample values for Meta:</p>
                      {Array.from({ length: countPlaceholders(templateForm.body_text) }, (_, i) => (
                        <input
                          key={i}
                          value={templateForm.body_examples[i] || ''}
                          onChange={(e) => {
                            const next = [...templateForm.body_examples];
                            next[i] = e.target.value;
                            setTemplateForm({ ...templateForm, body_examples: next });
                          }}
                          placeholder={`Sample for {{${i + 1}}}`}
                          className="w-full border rounded-lg px-2 py-1 text-sm"
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600">Footer (optional)</label>
                  <input
                    value={templateForm.footer_text}
                    onChange={(e) => setTemplateForm({ ...templateForm, footer_text: e.target.value.slice(0, 60) })}
                    placeholder="RoadServe"
                    className="w-full mt-0.5 border rounded-lg px-2 py-1.5 text-sm"
                  />
                </div>

                <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                  <label className="text-xs font-semibold text-gray-700">Buttons (optional)</label>
                  {templateForm.buttons.map((btn, idx) => (
                    <div key={idx} className="border rounded-lg p-2 bg-white space-y-1.5">
                      <div className="flex gap-2 items-center">
                        <select value={btn.type} onChange={(e) => updateTemplateButton(idx, { type: e.target.value })} className="border rounded px-2 py-1 text-xs flex-1">
                          <option value="URL">Visit website</option>
                          <option value="PHONE_NUMBER">Call phone</option>
                          <option value="QUICK_REPLY">Quick reply</option>
                        </select>
                        <button type="button" onClick={() => removeTemplateButton(idx)} className="text-red-400 p-1"><Trash2 size={14} /></button>
                      </div>
                      <input value={btn.text} onChange={(e) => updateTemplateButton(idx, { text: e.target.value })} placeholder="Button label" className="w-full border rounded px-2 py-1 text-sm" />
                      {btn.type === 'URL' && (
                        <input value={btn.url} onChange={(e) => updateTemplateButton(idx, { url: e.target.value })} placeholder="https://roadserve.in" className="w-full border rounded px-2 py-1 text-sm font-mono" />
                      )}
                      {btn.type === 'PHONE_NUMBER' && (
                        <input value={btn.phone_number} onChange={(e) => updateTemplateButton(idx, { phone_number: e.target.value })} placeholder="+917909999427" className="w-full border rounded px-2 py-1 text-sm" />
                      )}
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => addTemplateButton('URL')} className="text-xs px-2 py-1 border rounded-md bg-white">+ URL</button>
                    <button type="button" onClick={() => addTemplateButton('PHONE_NUMBER')} className="text-xs px-2 py-1 border rounded-md bg-white">+ Phone</button>
                    <button type="button" onClick={() => addTemplateButton('QUICK_REPLY')} className="text-xs px-2 py-1 border rounded-md bg-white">+ Quick reply</button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={createTemplate}
                  disabled={templateSubmitting}
                  className="w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-50 sticky bottom-0"
                >
                  {templateSubmitting ? <RefreshCw size={16} className="animate-spin" /> : (isEditingTemplate ? <Pencil size={16} /> : <Plus size={16} />)}
                  {isEditingTemplate ? 'Save edit to Meta' : 'Submit to Meta for approval'}
                </button>
              </div>

              <div className="xl:sticky xl:top-4">
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs font-semibold text-gray-700 mb-3 text-center">Live preview</p>
                  <WhatsAppTemplatePreview
                    headerType={templateForm.header_type}
                    headerText={templateForm.header_text}
                    headerTextExamples={templateForm.header_text_examples}
                    headerMediaName={templateForm.header_media_name}
                    bodyText={templateForm.body_text}
                    bodyExamples={templateForm.body_examples}
                    footerText={templateForm.footer_text}
                    buttons={templateForm.buttons}
                  />
                  {templateForm.name && (
                    <p className="text-center text-[10px] text-gray-400 mt-3 font-mono">{templateForm.name} · {templateForm.language}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI */}
      {section === 'ai' && aiMemory && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="font-semibold">AI Knowledge Base</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  From app.roadserve.in — RSA emergencies + membership sales
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!aiMemory.global_enabled} onChange={(e) => setAiMemory({ ...aiMemory, global_enabled: e.target.checked })} />
                Global AI ON
              </label>
            </div>
            <textarea
              value={aiMemory.system_prompt || ''}
              onChange={(e) => setAiMemory({ ...aiMemory, system_prompt: e.target.value })}
              rows={22}
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono leading-relaxed"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={saveAi} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm">
                Save AI Memory
              </button>
              <button
                type="button"
                onClick={async () => {
                  const next = { ...aiMemory, system_prompt: ROADSERVE_WHATSAPP_AI_PROMPT };
                  setAiMemory(next);
                  try {
                    await WhatsAppAutomationService.saveAiMemory(next);
                    flash('Marketing-site knowledge applied & saved');
                  } catch (e) {
                    flash(e.message || 'Applied in UI — save failed', true);
                  }
                }}
                className="px-4 py-2 border border-slate-200 bg-slate-50 text-slate-700 rounded-lg text-sm hover:bg-slate-100"
              >
                Reset from marketing site
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold mb-1">Test AI Reply</h2>
            <p className="text-xs text-slate-500 mb-3">Try: emergency tow, plan price, or “monthly vs yearly”</p>
            <textarea value={aiTestMsg} onChange={(e) => setAiTestMsg(e.target.value)} rows={4} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Sample customer message..." />
            <button onClick={testAi} className="mt-2 px-4 py-2 bg-gray-800 text-white rounded-lg text-sm flex items-center gap-1"><TestTube2 size={16} /> Preview</button>
            {aiTestReply && <div className="mt-4 p-4 bg-emerald-50 rounded-lg text-sm whitespace-pre-wrap">{aiTestReply}</div>}
          </div>
        </div>
      )}

      {accountToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete automation account?</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Ye number admin panel se hata diya jayega. Bulk messages aur automation rules is account par kaam nahi karenge.
                </p>
              </div>
            </div>
            <div className="mb-5 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm">
              <p className="font-semibold text-slate-900">
                {accountDetailsMap[accountToDelete.id]?.verified_name || accountToDelete.name}
              </p>
              <p className="mt-1 text-slate-600 tabular-nums">
                {accountDetailsMap[accountToDelete.id]?.display_phone_number
                  || accountToDelete.display_phone
                  || accountToDelete.phone_number_id}
              </p>
              {accountToDelete.is_default && (
                <p className="mt-2 text-xs font-medium text-amber-700">
                  Ye default account hai — delete karne ke baad koi aur default set karna hoga.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelDeleteAccount}
                disabled={!!deletingAccountId}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteAccount}
                disabled={!!deletingAccountId}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deletingAccountId ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
